import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from 'src/containers/NavBar';
import Flex from 'src/components/Flex';
import Button from 'src/components/Button';
import Card from 'src/components/Card';
import Input from 'src/components/Input';
import Icon from 'src/components/Icon';
import SideModal from 'src/components/SideModal';
import ModalHeader from 'src/components/ModalHeader';
import ModalFooter from 'src/components/ModalFooter';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { ICONS, ROUTES } from 'src/utils/constants';
import * as variables from 'src/styles/variables';
import { parseError } from 'src/utils';
import { ToastStoreObject } from 'src/stores/ToastStore';
import {
    DocumentScanCaptureMode,
    DocumentScanSupportedEntityType,
    DocumentScanTypeAdminSummary,
    DocumentScanTypeFacilityAvailabilitySummary,
    DocumentScanTypeTestResult,
    DocumentScanTypeVersionSummary,
    FacilityAvailabilityMode,
    FacilityDocumentScanTypeAvailabilitySummary,
    useAddDocumentScanTypeFormDefinition,
    useAdminDocumentScanType,
    useCreateDocumentScanTypeDraft,
    useCreateDocumentScanTypeFacilityOverrideDraft,
    useDiscardDocumentScanTypeFacilityOverrideDraft,
    useDocumentScanTypeFormDefinitions,
    useDocumentScanTypeFacilityAvailability,
    useEnableFacilityDocumentScanTypes,
    usePublishDocumentScanTypeFacilityOverrideDraft,
    usePublishDocumentScanTypeDraft,
    useDeactivateDocumentScanTypeFormDefinition,
    useTestDocumentScanTypeVersion,
    useUpdateDocumentScanTypeSettings,
    useUpdateDocumentScanTypeFacilityAvailability,
    useUpdateDocumentScanTypeFacilityOverrideDraft,
    useUpdateDocumentScanTypeDraft,
} from 'src/queries/useDocumentScanTypesAdmin';
import { getFormDefinitions } from 'src/api/encounters';
import * as facilitiesApi from 'src/api/facilities';
import { validateEncounterEntityMappingSchema } from 'common/lib/model/documentScan/encounterEntityMappingSchema';
import {
    EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_MODIFIERS,
    getExtractionSchemaFieldNames,
    validateExtractionEncounterNumberTemplate,
} from 'common/lib/model/documentScan/extractionEncounterNumberTemplate';
import styles from './styles.module.scss';

interface DocumentTypeDetailProps {
    documentScanTypeId: string;
}

interface DraftFormState {
    name: string;
    description: string;
    promptText: string;
    extractionSchemaText: string;
    entityMappingSchemaText: string;
    captureMode: DocumentScanCaptureMode;
    captureConfigText: string;
    allowEntityCreation: boolean;
    entityCreationConfigText: string;
    updateLinkedEntity: boolean;
    requiresReviewBeforeEntityUpdate: boolean;
}

type DetailSection = 'overview' | 'extraction' | 'mapping';
type OverrideSection = 'prompt' | 'extraction' | 'mapping';
type NativeDocumentCropProfile = 'document' | 'card';
const LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME = 'encounterScanEnabled';

interface CaptureSlot {
    slotKey: string;
    label: string;
    required: boolean;
    maxImages: number;
}

interface ExtractionField {
    fieldKey: string;
    type: string;
    description: string;
}

interface FacilityOverrideFormState {
    sectionEnabled: Record<OverrideSection, boolean>;
    promptTextMode: 'replace' | 'append';
    promptText: string;
    extractionSchemaText: string;
    entityMappingSchemaText: string;
    entityCreationConfigText: string;
}

interface FacilityEnablementConfirmation {
    facility: FacilityDocumentScanTypeAvailabilitySummary;
    disableLegacyCaptureAi: boolean;
}

type FacilityOverrideFormUpdate =
    | Partial<FacilityOverrideFormState>
    | ((current: FacilityOverrideFormState) => FacilityOverrideFormState);

interface CaptureConfigForm {
    captureMode: DocumentScanCaptureMode;
    captureConfigText: string;
}

interface ExtractionSchemaForm {
    extractionSchemaText: string;
}

interface MappingConfigForm {
    entityMappingSchemaText: string;
    extractionSchemaText: string;
    entityCreationConfigText: string;
    allowEntityCreation: boolean;
}

const detailSections: Array<{ id: DetailSection; title: string; description: string }> = [
    { id: 'overview', title: 'Overview', description: 'Capture rules' },
    { id: 'extraction', title: 'Extraction', description: 'Prompt and extracted fields' },
    { id: 'mapping', title: 'Mapping', description: 'Field updates' },
];

function formatValue(value: string | null | undefined) {
    if (!value) {
        return 'None';
    }
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatCaptureMode(captureMode: DocumentScanCaptureMode | null | undefined) {
    if (captureMode === 'unnamed_pages') {
        return 'Page Images';
    }
    if (captureMode === 'named_slots') {
        return 'Labeled Images';
    }
    return formatValue(captureMode);
}

function getEntityDisplayName(entityType: string | null | undefined) {
    return entityType ? formatValue(entityType) : 'Record';
}

function getEntityLowerName(entityType: string | null | undefined) {
    return getEntityDisplayName(entityType).toLowerCase();
}

function getIndefiniteArticle(value: string) {
    return /^[aeiou]/i.test(value.trim()) ? 'an' : 'a';
}

function getEntityMappingTitle(entityType: string | null | undefined) {
    if (entityType === 'encounter') {
        return 'Encounter Mapping';
    }
    if (entityType === 'encounter_form') {
        return 'Form Mapping';
    }
    return `${getEntityDisplayName(entityType)} Mapping`;
}

function getEntityMappingSettingsTabLabel(entityType: string | null | undefined) {
    if (entityType === 'encounter') {
        return 'Encounter Settings';
    }
    if (entityType === 'encounter_form') {
        return 'Form Settings';
    }
    return `${getEntityDisplayName(entityType)} Settings`;
}

function getEntityMappingSchemaLabel(entityType: string | null | undefined) {
    if (entityType === 'encounter') {
        return 'Encounter Mapping Schema';
    }
    if (entityType === 'encounter_form') {
        return 'Form Mapping Schema';
    }
    return `${getEntityDisplayName(entityType)} Mapping Schema`;
}

function getEntityMappingDescription(entityType: string | null | undefined) {
    const entityName = getEntityLowerName(entityType);
    return `Control when scans create or update ${entityName}s, and which ${entityName} fields Capture AI may write.`;
}

function getEntityMappingOverrideDescription(entityType: string | null | undefined) {
    const entityName = getEntityLowerName(entityType);
    return `Override which ${entityName} fields Capture AI may update for this facility.`;
}

function getEntityMappingSchemaHelpText(entityType: string | null | undefined) {
    if (entityType === 'encounter') {
        return (
            'Use a JSON Schema object with a non-empty properties object. For encounters, every property must be ' +
            'one of the canonical encounter fields and must keep the canonical field shape; title and description ' +
            'can be customized.'
        );
    }
    return 'Use a JSON Schema object with a non-empty properties object for the mapped record fields.';
}

function getEntityBehaviorCopy(entityType: string | null | undefined) {
    if (entityType === 'encounter_form') {
        return {
            createLabel: 'Can create associated forms',
            createDescription:
                'Makes this Capture AI document type available from the encounter form list for its associated forms.',
            updateLabel: 'Can update existing associated forms',
            updateDescription:
                'Makes this Capture AI document type available from an open encounter form that has an active association.',
        };
    }

    const entityName = getEntityLowerName(entityType);
    const article = getIndefiniteArticle(entityName);

    return {
        createLabel: `Allow creating ${article} ${entityName} from a scan`,
        createDescription: `Creates a new ${entityName} when no existing ${entityName} match is found.`,
        updateLabel: `Update ${entityName} from new scans`,
        updateDescription: `When a matching ${entityName} is found, or a scan is added to an existing ${entityName}, mapped extracted fields update that ${entityName}.`,
    };
}

function getVersionHistoryOptions(scanType: DocumentScanTypeAdminSummary | undefined) {
    if (!scanType) {
        return [];
    }

    const versions = [scanType.draftVersion, scanType.publishedVersion, ...(scanType.versions || [])].filter(
        (version): version is DocumentScanTypeVersionSummary => !!version,
    );
    const uniqueVersions = new Map<string, DocumentScanTypeVersionSummary>();
    versions.forEach((version) => uniqueVersions.set(version.documentScanTypeVersionId, version));
    return Array.from(uniqueVersions.values()).sort((a, b) => b.versionNumber - a.versionNumber);
}

function getFacilityOverrideHistoryOptions(facility: FacilityDocumentScanTypeAvailabilitySummary | null) {
    if (!facility) {
        return [];
    }

    const versions = [facility.draftVersion, facility.publishedVersion, ...(facility.versions || [])].filter(
        (version): version is NonNullable<FacilityDocumentScanTypeAvailabilitySummary['draftVersion']> => !!version,
    );
    const uniqueVersions = new Map<string, NonNullable<FacilityDocumentScanTypeAvailabilitySummary['draftVersion']>>();
    versions.forEach((version) => uniqueVersions.set(version.facilityDocumentScanTypeVersionId, version));
    return Array.from(uniqueVersions.values()).sort((a, b) => b.versionNumber - a.versionNumber);
}

function toJsonText(value: unknown, fallback: unknown = {}) {
    return JSON.stringify(value ?? fallback, null, 2);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function hasOwn(value: Record<string, unknown>, key: string) {
    return Object.prototype.hasOwnProperty.call(value, key);
}

function safeParseJsonRecord(text: string, fallback: Record<string, unknown> = {}) {
    try {
        const parsed = JSON.parse(text || '{}');
        return isRecord(parsed) ? parsed : fallback;
    } catch {
        return fallback;
    }
}

function parseJsonRecord(text: string, fieldName: string, allowNull = false): Record<string, unknown> | null {
    const trimmed = text.trim();
    if (!trimmed) {
        return allowNull ? null : {};
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(trimmed);
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(`${fieldName} contains invalid JSON: ${message}`);
    }
    if (parsed === null && allowNull) {
        return null;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(`${fieldName} must be a JSON object${allowNull ? ' or null' : ''}`);
    }
    return parsed as Record<string, unknown>;
}

function getErrorMessage(error: unknown) {
    return error instanceof Error ? error.message : parseError(error as any);
}

function getFacilityVersion(
    facility: FacilityDocumentScanTypeAvailabilitySummary,
): FacilityDocumentScanTypeAvailabilitySummary['draftVersion'] | FacilityDocumentScanTypeAvailabilitySummary['publishedVersion'] {
    return facility.draftVersion || facility.publishedVersion;
}

function buildDraftForm(version: DocumentScanTypeVersionSummary): DraftFormState {
    return {
        name: version.displayName || '',
        description: version.description || '',
        promptText: version.promptText || '',
        extractionSchemaText: toJsonText(version.extractionSchema, { type: 'object', additionalProperties: true }),
        entityMappingSchemaText: toJsonText(version.entityMappingSchema, null),
        captureMode: version.captureMode,
        captureConfigText: toJsonText(version.captureConfig, {}),
        allowEntityCreation: !!version.allowEntityCreation,
        entityCreationConfigText: toJsonText(version.entityCreationConfig, null),
        updateLinkedEntity: !!version.updateLinkedEntity,
        requiresReviewBeforeEntityUpdate: !!version.requiresReviewBeforeEntityUpdate,
    };
}

function parseEntityMappingSchemaText(
    text: string,
    fieldName: string,
    supportedEntityType: DocumentScanSupportedEntityType,
    allowEntityCreation: boolean,
): Record<string, unknown> | null {
    const schema = parseJsonRecord(text, fieldName, supportedEntityType !== 'encounter');
    if (supportedEntityType !== 'encounter') {
        return schema;
    }

    const validation = validateEncounterEntityMappingSchema(schema, { allowEntityCreation });
    if (!validation.valid || !validation.normalizedSchema) {
        throw new Error(`${fieldName} is invalid: ${validation.errors.join('; ')}`);
    }
    return validation.normalizedSchema;
}

function parseEntityCreationConfigText(
    text: string,
    extractionSchema: Record<string, unknown> | null,
    supportedEntityType: DocumentScanSupportedEntityType,
): Record<string, unknown> | null {
    const config = parseJsonRecord(text, 'Entity creation config', true);
    if (!config || supportedEntityType !== 'encounter' || !hasOwn(config, 'encounterNumberTemplate')) {
        return config;
    }

    const validation = validateExtractionEncounterNumberTemplate({
        template: config.encounterNumberTemplate,
        extractionSchema,
    });
    if (!validation.valid) {
        throw new Error(`Encounter Number Template is invalid: ${validation.errors.join('; ')}`);
    }

    return {
        ...config,
        encounterNumberTemplate: validation.normalizedTemplate,
    };
}

function getEncounterNumberTemplate(form: MappingConfigForm | null): string {
    const config = safeParseJsonRecord(form?.entityCreationConfigText || '{}');
    return typeof config.encounterNumberTemplate === 'string' ? config.encounterNumberTemplate : '';
}

function setEncounterNumberTemplate<T extends MappingConfigForm>(form: T, encounterNumberTemplate: string): T {
    const config = safeParseJsonRecord(form.entityCreationConfigText || '{}');
    return {
        ...form,
        entityCreationConfigText: toJsonText(
            {
                ...config,
                preserveLegacyDuplicateEncounterBehavior: true,
                encounterNumberTemplate: encounterNumberTemplate.trim() ? encounterNumberTemplate : null,
            },
            null,
        ),
    };
}

function getEncounterNumberTemplateErrors(
    form: MappingConfigForm,
    supportedEntityType: DocumentScanSupportedEntityType,
): string[] {
    if (supportedEntityType !== 'encounter') {
        return [];
    }

    try {
        const extractionSchema = parseJsonRecord(form.extractionSchemaText, 'Extraction schema', true);
        parseEntityCreationConfigText(form.entityCreationConfigText, extractionSchema, supportedEntityType);
        return [];
    } catch (error) {
        return [getErrorMessage(error)];
    }
}

function getEntityMappingSchemaErrors(
    text: string,
    supportedEntityType: DocumentScanSupportedEntityType,
    allowEntityCreation: boolean,
): string[] {
    try {
        parseEntityMappingSchemaText(
            text,
            getEntityMappingSchemaLabel(supportedEntityType),
            supportedEntityType,
            allowEntityCreation,
        );
        return [];
    } catch (error) {
        return [getErrorMessage(error)];
    }
}

function getDraftPayload(form: DraftFormState, supportedEntityType: DocumentScanSupportedEntityType) {
    const extractionSchema = parseJsonRecord(form.extractionSchemaText, 'Extraction schema', true);
    return {
        promptText: form.promptText,
        extractionSchema,
        entityMappingSchema: parseEntityMappingSchemaText(
            form.entityMappingSchemaText,
            getEntityMappingSchemaLabel(supportedEntityType),
            supportedEntityType,
            form.allowEntityCreation,
        ),
        captureMode: form.captureMode,
        captureConfig: getDraftCaptureConfig(form),
        allowEntityCreation: form.allowEntityCreation,
        entityCreationConfig: parseEntityCreationConfigText(
            form.entityCreationConfigText,
            extractionSchema,
            supportedEntityType,
        ),
        updateLinkedEntity: form.updateLinkedEntity,
        requiresReviewBeforeEntityUpdate: form.requiresReviewBeforeEntityUpdate,
    };
}

function getCaptureConfig(form: CaptureConfigForm | null) {
    return safeParseJsonRecord(form?.captureConfigText || '{}');
}

function updateCaptureConfig<T extends CaptureConfigForm>(form: T, captureConfig: Record<string, unknown>): T {
    return {
        ...form,
        captureConfigText: toJsonText(captureConfig, {}),
    };
}

function getPageCount(form: CaptureConfigForm | null, key: 'minImages' | 'maxImages') {
    const config = getCaptureConfig(form);
    const value = Number(config[key] ?? (key === 'minImages' ? 1 : config.minImages ?? 1));
    return Number.isFinite(value) && value > 0 ? value : 1;
}

function getMobileImageProcessingConfig(config: Record<string, unknown>) {
    return isRecord(config.mobileImageProcessing) ? config.mobileImageProcessing : {};
}

function isNativeDocumentCropProfile(value: unknown): value is NativeDocumentCropProfile {
    return value === 'document' || value === 'card';
}

function getNativeDocumentCropEnabled(form: CaptureConfigForm | null) {
    const config = getCaptureConfig(form);
    const mobileImageProcessing = getMobileImageProcessingConfig(config);
    return mobileImageProcessing.nativeDocumentCrop === true;
}

function getNativeDocumentCropProfile(form: CaptureConfigForm | null): NativeDocumentCropProfile {
    const config = getCaptureConfig(form);
    const mobileImageProcessing = getMobileImageProcessingConfig(config);
    const profile = mobileImageProcessing.nativeDocumentCropProfile;
    return isNativeDocumentCropProfile(profile) ? profile : 'document';
}

function setNativeDocumentCropConfig<T extends CaptureConfigForm>(
    form: T,
    enabled: boolean,
    profile: NativeDocumentCropProfile = getNativeDocumentCropProfile(form),
): T {
    const config = getCaptureConfig(form);
    const mobileImageProcessing = getMobileImageProcessingConfig(config);
    const nextConfig = { ...config };
    const nextMobileImageProcessing = { ...mobileImageProcessing };

    delete nextConfig.nativeDocumentCrop;
    delete nextConfig.nativeDocumentCropProfile;
    delete nextConfig.nativeDocumentCropSettings;
    delete nextMobileImageProcessing.nativeDocumentCrop;
    delete nextMobileImageProcessing.nativeDocumentCropProfile;
    delete nextMobileImageProcessing.nativeDocumentCropSettings;

    if (enabled) {
        nextConfig.mobileImageProcessing = {
            ...nextMobileImageProcessing,
            nativeDocumentCrop: true,
            nativeDocumentCropProfile: profile,
        };
    } else if (Object.keys(nextMobileImageProcessing).length > 0) {
        nextConfig.mobileImageProcessing = nextMobileImageProcessing;
    } else {
        delete nextConfig.mobileImageProcessing;
    }

    return updateCaptureConfig(form, nextConfig);
}

function toSnakeCaseKey(value: string) {
    return value
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function getCaptureSlots(form: CaptureConfigForm | null): CaptureSlot[] {
    const config = getCaptureConfig(form);
    const slots = Array.isArray(config.slots) ? config.slots : [];
    return slots.filter(isRecord).map((slot, index) => ({
        slotKey: `${slot.slotKey || `slot_${index + 1}`}`,
        label: `${slot.label || slot.slotKey || `Slot ${index + 1}`}`,
        required: slot.required !== false,
        maxImages: Number(slot.maxImages || 1) || 1,
    }));
}

function normalizeCaptureSlotForSave(slot: CaptureSlot, index: number) {
    const label = slot.label || `Label ${index + 1}`;
    const slotKey = toSnakeCaseKey(slot.slotKey) || toSnakeCaseKey(label) || `slot_${index + 1}`;
    return {
        slotKey,
        label,
        required: slot.required !== false,
        maxImages: 1,
    };
}

function isAutoGeneratedSlotKey(slot: CaptureSlot, index: number) {
    const currentKey = toSnakeCaseKey(slot.slotKey);
    const labelKey = toSnakeCaseKey(slot.label);
    return !currentKey || currentKey === `slot_${index + 1}` || (!!labelKey && currentKey === labelKey);
}

function getDraftCaptureConfig(form: DraftFormState) {
    const captureConfig = parseJsonRecord(form.captureConfigText, 'Capture config');
    if (form.captureMode !== 'named_slots') {
        return captureConfig;
    }

    return {
        ...captureConfig,
        slots: getCaptureSlots(form).map(normalizeCaptureSlotForSave),
    };
}

function setCaptureSlots<T extends CaptureConfigForm>(form: T, slots: CaptureSlot[]): T {
    return updateCaptureConfig(form, {
        ...getCaptureConfig(form),
        slots: slots.map(normalizeCaptureSlotForSave),
    });
}

function getExtractionFields(form: ExtractionSchemaForm | null): ExtractionField[] {
    const schema = safeParseJsonRecord(form?.extractionSchemaText || '{}', {
        type: 'object',
        additionalProperties: true,
        properties: {},
    });
    const properties = isRecord(schema.properties) ? schema.properties : {};
    return Object.entries(properties).map(([fieldKey, value]) => {
        const field = isRecord(value) ? value : {};
        return {
            fieldKey,
            type: `${field.type || 'string'}`,
            description: `${field.description || ''}`,
        };
    });
}

function setExtractionFields<T extends ExtractionSchemaForm>(form: T, fields: ExtractionField[]): T {
    const schema = safeParseJsonRecord(form.extractionSchemaText, {
        type: 'object',
        additionalProperties: true,
        properties: {},
    });
    const properties = fields.reduce((result, field) => {
        const fieldKey = field.fieldKey.trim();
        if (!fieldKey) {
            return result;
        }
        result[fieldKey] = {
            type: field.type || 'string',
            ...(field.description.trim() ? { description: field.description.trim() } : {}),
        };
        return result;
    }, {} as Record<string, unknown>);

    return {
        ...form,
        extractionSchemaText: toJsonText(
            {
                ...schema,
                type: schema.type || 'object',
                additionalProperties: schema.additionalProperties ?? true,
                properties,
            },
            {},
        ),
    };
}

function getEntityMappingFieldNames(form: MappingConfigForm | null): string[] {
    const mapping = safeParseJsonRecord(form?.entityMappingSchemaText || '{}');
    const properties = isRecord(mapping.properties) ? mapping.properties : {};
    return Object.keys(properties);
}

function getOverrideFieldCount(overrides: Record<string, unknown> | null | undefined) {
    return overrides ? Object.keys(overrides).length : 0;
}

function getFacilityStatus(
    mode: FacilityAvailabilityMode,
    selectedFacilityIds: number[],
    facility: FacilityDocumentScanTypeAvailabilitySummary,
) {
    const version = getFacilityVersion(facility);
    const selectedByAvailability = mode === 'all_facilities' || selectedFacilityIds.includes(facility.facilityId);
    const selected = facility.captureAiScanTypesEnabled && selectedByAvailability;
    const overrideCount = getOverrideFieldCount(version?.overrides);
    const availability = !facility.captureAiScanTypesEnabled
        ? 'CaptureAI v2 Disabled'
        : selectedByAvailability
        ? 'Enabled'
        : 'Disabled';
    const lastChange = facility.draftVersion
        ? `Draft v${facility.draftVersion.versionNumber}`
        : facility.publishedVersion
        ? `Published v${facility.publishedVersion.versionNumber}`
        : 'None';

    return { selected, overrideCount, availability, lastChange };
}

function getFacilityLabel(facility: FacilityDocumentScanTypeAvailabilitySummary | null) {
    if (!facility) {
        return 'Facility';
    }
    return facility.facilityName || `Facility ${facility.facilityId}`;
}

function getFacilityScopeSubtitle(facility: FacilityDocumentScanTypeAvailabilitySummary) {
    if (!facility.captureAiScanTypesEnabled) {
        return 'Enable Document Types in Facility Settings';
    }
    if (facility.publishedVersion?.hasOverride) {
        return `Published Override v${facility.publishedVersion.versionNumber}`;
    }
    return 'No Override, Uses Org Defaults';
}

function facilityHasOverrideState(facility: FacilityDocumentScanTypeAvailabilitySummary) {
    return !!facility.facilityDocumentScanTypeId || !!facility.publishedVersion || !!facility.draftVersion || facility.hasDraft;
}

function getFacilityDraftIndicatorLabel(facility: FacilityDocumentScanTypeAvailabilitySummary) {
    const hasPendingDraft = !!facility.draftVersion || facility.hasDraft;
    if (!hasPendingDraft) {
        return null;
    }
    if (facility.overrideStatus === 'removal_draft' || (facility.draftVersion && !facility.draftVersion.hasOverride)) {
        return 'Pending removal draft';
    }
    return 'Pending override draft';
}

function getFacilityScopeOptionLabel(facility: FacilityDocumentScanTypeAvailabilitySummary) {
    const label = getFacilityLabel(facility);
    const draftLabel = getFacilityDraftIndicatorLabel(facility);
    return [label, getFacilityScopeSubtitle(facility), draftLabel].filter(Boolean).join(' ');
}

function hasEnabledOverrideSections(form: FacilityOverrideFormState | null) {
    return !!form && (form.sectionEnabled.prompt || form.sectionEnabled.extraction || form.sectionEnabled.mapping);
}

function SectionShell({
    title,
    description,
    children,
}: {
    title: string;
    description?: string;
    children: React.ReactNode;
}) {
    return (
        <section className={styles.workspacePanel}>
            <div className={styles.workspacePanelHeader}>
                <h2 className={styles.sectionTitle}>{title}</h2>
                {description ? <p className={styles.sectionDescription}>{description}</p> : null}
            </div>
            {children}
        </section>
    );
}

function StatusChip({
    text,
    children,
    tone = 'neutral',
    'data-test-id': dataTestId,
}: {
    text?: string;
    children?: React.ReactNode;
    tone?: 'neutral' | 'good' | 'warning' | 'danger';
    'data-test-id'?: string;
}) {
    return (
        <span
            className={classNames(styles.statusChip, {
                [styles.goodChip]: tone === 'good',
                [styles.warningChip]: tone === 'warning',
                [styles.dangerChip]: tone === 'danger',
            })}
            data-test-id={dataTestId}
        >
            {children || text}
        </span>
    );
}

function HeaderIconButton({
    title,
    icon,
    onClick,
    disabled,
    'data-test-id': dataTestId,
}: {
    title: string;
    icon: React.ReactNode;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
    'data-test-id'?: string;
}) {
    return (
        <Button
            type="outline"
            title={title}
            aria-label={title}
            onClick={onClick}
            disabled={disabled}
            className={styles.iconButton}
            data-test-id={dataTestId}
        >
            {icon}
        </Button>
    );
}

interface FacilityFormDefinitionOption {
    formDefnId: number;
    formDefnNm?: string;
    formDefnTitle?: string;
    formDefnVer?: number;
    facId?: number;
    facilityId: number;
    facilityName: string | null;
}

function AssociatedFormsSection({
    documentScanTypeId,
    facilities,
}: {
    documentScanTypeId: string;
    facilities: FacilityDocumentScanTypeAvailabilitySummary[];
}) {
    const { data: associations = [], isLoading, refetch } = useDocumentScanTypeFormDefinitions(documentScanTypeId);
    const { mutateAsync: addAssociation, isLoading: isAddingAssociation } = useAddDocumentScanTypeFormDefinition();
    const { mutateAsync: removeAssociation, isLoading: isRemovingAssociation } =
        useDeactivateDocumentScanTypeFormDefinition();
    const enabledFacilities = useMemo(() => facilities.filter((facility) => facility.availabilityEnabled), [facilities]);
    const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
    const [formSearch, setFormSearch] = useState('');
    const [availableForms, setAvailableForms] = useState<FacilityFormDefinitionOption[]>([]);
    const [isLoadingForms, setIsLoadingForms] = useState(false);
    const [selectedFormDefinitionIds, setSelectedFormDefinitionIds] = useState<number[]>([]);

    useEffect(() => {
        if (!isAddFormModalOpen) {
            return;
        }

        setFormSearch('');
        setSelectedFormDefinitionIds([]);
        if (!enabledFacilities.length) {
            setAvailableForms([]);
            return;
        }

        let active = true;
        setIsLoadingForms(true);
        Promise.allSettled(
            enabledFacilities.map(async (facility) => {
                const forms = await getFormDefinitions(facility.facilityId);
                return (forms || []).map((formDefinition: FacilityFormDefinitionOption) => ({
                    ...formDefinition,
                    facilityId: facility.facilityId,
                    facilityName: facility.facilityName,
                }));
            }),
        )
            .then((results) => {
                if (!active) {
                    return;
                }
                const failedFacilityCount = results.filter((result) => result.status === 'rejected').length;
                if (failedFacilityCount) {
                    ToastStoreObject.show(`Unable to load forms for ${failedFacilityCount} facilities.`);
                }
                setAvailableForms(results.flatMap((result) => (result.status === 'fulfilled' ? result.value : [])));
            })
            .catch((error) => ToastStoreObject.show(getErrorMessage(error)))
            .finally(() => {
                if (active) {
                    setIsLoadingForms(false);
                }
            });

        return () => {
            active = false;
        };
    }, [enabledFacilities, isAddFormModalOpen]);

    const associatedFormIds = useMemo(
        () => new Set(associations.map((association) => association.formDefinitionId)),
        [associations],
    );
    const selectedFormDefinitionIdSet = useMemo(
        () => new Set(selectedFormDefinitionIds),
        [selectedFormDefinitionIds],
    );
    const filteredAvailableForms = useMemo(() => {
        const search = formSearch.trim().toLowerCase();
        return availableForms
            .filter((formDefinition) => !associatedFormIds.has(formDefinition.formDefnId))
            .filter((formDefinition) => {
                if (!search) {
                    return true;
                }
                return `${formDefinition.facilityName || ''} ${formDefinition.formDefnTitle || ''} ${
                    formDefinition.formDefnNm || ''
                } ${formDefinition.formDefnId}`
                    .toLowerCase()
                    .includes(search);
            })
            .sort((left, right) => {
                const leftText = `${left.facilityName || ''} ${left.formDefnTitle || left.formDefnNm || ''}`;
                const rightText = `${right.facilityName || ''} ${right.formDefnTitle || right.formDefnNm || ''}`;
                return leftText.localeCompare(rightText);
            });
    }, [associatedFormIds, availableForms, formSearch]);

    const closeAddFormModal = () => {
        setIsAddFormModalOpen(false);
        setFormSearch('');
        setSelectedFormDefinitionIds([]);
    };

    const toggleSelectedForm = (formDefinitionId: number, checked: boolean) => {
        setSelectedFormDefinitionIds((current) => {
            if (checked) {
                return Array.from(new Set([...current, formDefinitionId]));
            }
            return current.filter((id) => id !== formDefinitionId);
        });
    };

    const addSelectedForms = async () => {
        const formDefinitionIds = selectedFormDefinitionIds.filter((id) => !associatedFormIds.has(id));
        if (!formDefinitionIds.length) {
            return;
        }

        try {
            const results = await Promise.allSettled(
                formDefinitionIds.map((formDefinitionId) => addAssociation({ documentScanTypeId, formDefinitionId })),
            );
            const failedFormDefinitionIds = results
                .map((result, index) => (result.status === 'rejected' ? formDefinitionIds[index] : null))
                .filter((formDefinitionId): formDefinitionId is number => formDefinitionId !== null);
            const addedCount = formDefinitionIds.length - failedFormDefinitionIds.length;

            if (addedCount) {
                ToastStoreObject.show(addedCount === 1 ? 'Form associated' : `${addedCount} forms associated`);
                refetch();
            }
            if (failedFormDefinitionIds.length) {
                ToastStoreObject.show(
                    `Unable to associate ${failedFormDefinitionIds.length} ${
                        failedFormDefinitionIds.length === 1 ? 'form' : 'forms'
                    }.`,
                );
                setSelectedFormDefinitionIds(failedFormDefinitionIds);
                return;
            }
            closeAddFormModal();
        } catch (error) {
            ToastStoreObject.show(getErrorMessage(error));
        }
    };

    const removeForm = async (documentScanTypeFormDefinitionId: string) => {
        try {
            await removeAssociation({ documentScanTypeId, documentScanTypeFormDefinitionId });
            ToastStoreObject.show('Form association removed');
            refetch();
        } catch (error) {
            ToastStoreObject.show(getErrorMessage(error));
        }
    };

    return (
        <SectionShell
            title="Associated Forms"
            description="Form associations are settings. Disabled facilities keep their mappings but cannot use them until re-enabled."
        >
            <div className={styles.associationToolbar}>
                <Button
                    type="outline"
                    text="Add Form"
                    disabled={!enabledFacilities.length}
                    onClick={() => setIsAddFormModalOpen(true)}
                    data-test-id="open-add-associated-form-modal"
                />
            </div>
            {isLoading ? (
                <div className={styles.emptyState}>Loading associated forms...</div>
            ) : associations.length ? (
                <div className={styles.associationTable} data-test-id="associated-forms-table">
                    <div className={styles.associationTableHeader}>
                        <span>Form</span>
                        <span>Facility</span>
                        <span>Version</span>
                        <span>Status</span>
                        <span />
                    </div>
                    {associations.map((association) => {
                        const statusText = association.effectiveActive
                            ? 'Available'
                            : association.facilityAvailabilityEnabled
                            ? 'Unavailable'
                            : 'Disabled by facility';
                        const statusTone = association.effectiveActive
                            ? 'good'
                            : association.facilityAvailabilityEnabled
                            ? 'warning'
                            : 'neutral';
                        return (
                            <div className={styles.associationTableRow} key={association.documentScanTypeFormDefinitionId}>
                                <div>
                                    <strong>{association.formDefinitionTitle || 'Untitled Form'}</strong>
                                    <span>{association.formDefinitionName || `Form ${association.formDefinitionId}`}</span>
                                </div>
                                <span>{association.facilityName || `Facility ${association.facilityId}`}</span>
                                <span>{association.formDefinitionVersion || 'Current'}</span>
                                <div>
                                    <StatusChip text={statusText} tone={statusTone} />
                                </div>
                                <Button
                                    type="transparent"
                                    text="Remove"
                                    className={styles.removeButton}
                                    disabled={isRemovingAssociation}
                                    onClick={() => removeForm(association.documentScanTypeFormDefinitionId)}
                                    data-test-id={`remove-associated-form-${association.formDefinitionId}`}
                                />
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className={styles.emptyState}>No encounter forms are associated.</div>
            )}
            <SideModal isOpen={isAddFormModalOpen} isWide={true} onClose={closeAddFormModal}>
                <ModalHeader title="Add Form" onClose={closeAddFormModal} />
                <div className={styles.overrideModalBody} data-test-id="add-associated-form-modal">
                    {!enabledFacilities.length ? (
                        <div className={styles.emptyState}>Enable a facility before adding form associations.</div>
                    ) : (
                        <>
                            <CompactInput
                                label="Search Forms"
                                value={formSearch}
                                onChangeText={(value: string) => setFormSearch(value)}
                                placeholder="Search by facility, form name, or title"
                                data-test-id="associated-form-search-input"
                            />
                            <div
                                className={styles.associationPickerMeta}
                                data-test-id="associated-form-selection-summary"
                            >
                                {selectedFormDefinitionIds.length
                                    ? `${selectedFormDefinitionIds.length} ${
                                          selectedFormDefinitionIds.length === 1 ? 'form' : 'forms'
                                      } selected`
                                    : 'Select one or more forms to associate.'}
                            </div>
                            {isLoadingForms ? (
                                <div className={styles.emptyState}>Loading forms...</div>
                            ) : filteredAvailableForms.length ? (
                                <div className={styles.associationPickerList} data-test-id="associated-form-picker-list">
                                    {filteredAvailableForms.map((formDefinition) => {
                                        const selected = selectedFormDefinitionIdSet.has(formDefinition.formDefnId);
                                        return (
                                            <label
                                                key={`${formDefinition.facilityId}-${formDefinition.formDefnId}`}
                                                className={classNames(styles.associationPickerItem, {
                                                    [styles.associationPickerItemSelected]: selected,
                                                })}
                                                data-test-id={`add-associated-form-${formDefinition.formDefnId}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    disabled={isAddingAssociation}
                                                    className={styles.associationPickerCheckbox}
                                                    onChange={(event) =>
                                                        toggleSelectedForm(
                                                            formDefinition.formDefnId,
                                                            event.target.checked,
                                                        )
                                                    }
                                                    data-test-id={`select-associated-form-${formDefinition.formDefnId}`}
                                                />
                                                <div>
                                                    <strong>{formDefinition.formDefnTitle || 'Untitled Form'}</strong>
                                                    <span>
                                                        {formDefinition.formDefnNm ||
                                                            `Form ${formDefinition.formDefnId}`}
                                                    </span>
                                                </div>
                                                <span>
                                                    {formDefinition.facilityName || `Facility ${formDefinition.facilityId}`}
                                                </span>
                                                <span>
                                                    {formDefinition.formDefnVer
                                                        ? `v${formDefinition.formDefnVer}`
                                                        : 'Current'}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={styles.emptyState}>No unassociated forms match your search.</div>
                            )}
                        </>
                    )}
                </div>
                <ModalFooter>
                    <Flex direction="row" align="center" justify="end" self="stretch" className={styles.overrideModalFooter}>
                        <Button type="outline" text="Close" onClick={closeAddFormModal} />
                        <Button
                            type="primary"
                            text={
                                selectedFormDefinitionIds.length
                                    ? `Add Selected (${selectedFormDefinitionIds.length})`
                                    : 'Add Selected'
                            }
                            onClick={addSelectedForms}
                            disabled={!selectedFormDefinitionIds.length || isLoadingForms || isAddingAssociation}
                            isLoading={isAddingAssociation}
                            data-test-id="add-selected-associated-forms"
                        />
                    </Flex>
                </ModalFooter>
            </SideModal>
        </SectionShell>
    );
}

type CompactInputProps = Omit<React.ComponentProps<typeof Input>, 'label'> & {
    label: string;
    fieldClassName?: string;
    hideLabel?: boolean;
};

function CompactInput({
    label,
    required,
    fieldClassName,
    hideLabel,
    className,
    wrapClassName,
    multiLine,
    ...inputProps
}: CompactInputProps) {
    return (
        <label className={classNames(styles.field, fieldClassName)}>
            <span className={classNames(styles.fieldLabel, { [styles.srOnly]: hideLabel })}>
                {label}
                {required ? <span className={styles.requiredAsterisk}>*</span> : null}
            </span>
            <Input
                {...inputProps}
                required={required}
                multiLine={multiLine}
                className={classNames(styles.compactTextInput, className)}
                wrapClassName={classNames(styles.compactInputWrap, { [styles.compactTextareaWrap]: multiLine }, wrapClassName)}
            />
        </label>
    );
}

function OrgDefaultsSection({
    form,
    editable,
    onChange,
}: {
    form: DraftFormState;
    editable: boolean;
    onChange: (updates: Partial<DraftFormState> | ((current: DraftFormState) => DraftFormState)) => void;
}) {
    const slots = getCaptureSlots(form);
    const nativeDocumentCropEnabled = getNativeDocumentCropEnabled(form);
    const nativeDocumentCropProfile = getNativeDocumentCropProfile(form);

    const updatePageCount = (key: 'minImages' | 'maxImages', value: string) => {
        onChange((current) => {
            const config = getCaptureConfig(current);
            return updateCaptureConfig(current, {
                ...config,
                [key]: Number(value) || 1,
            });
        });
    };

    const updateSlot = (index: number, updates: Partial<CaptureSlot>) => {
        onChange((current) => {
            const nextSlots = getCaptureSlots(current).map((slot, slotIndex) =>
                slotIndex === index ? { ...slot, ...updates } : slot,
            );
            return setCaptureSlots(current, nextSlots);
        });
    };

    const updateSlotLabel = (index: number, label: string) => {
        onChange((current) => {
            const nextSlots = getCaptureSlots(current).map((slot, slotIndex) => {
                if (slotIndex !== index) {
                    return slot;
                }

                return {
                    ...slot,
                    label,
                    slotKey: isAutoGeneratedSlotKey(slot, index) ? toSnakeCaseKey(label) : slot.slotKey,
                    maxImages: 1,
                };
            });
            return setCaptureSlots(current, nextSlots);
        });
    };

    const updateSlotKey = (index: number, slotKey: string) => {
        updateSlot(index, { slotKey: toSnakeCaseKey(slotKey), maxImages: 1 });
    };

    const addSlot = () => {
        onChange((current) =>
            setCaptureSlots(current, [
                ...getCaptureSlots(current),
                { slotKey: '', label: '', required: true, maxImages: 1 },
            ]),
        );
    };

    const removeSlot = (index: number) => {
        onChange((current) => setCaptureSlots(current, getCaptureSlots(current).filter((_, slotIndex) => slotIndex !== index)));
    };

    return (
        <SectionShell title="Overview" description="Capture requirements for this document type version.">
            <div className={styles.formGrid}>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Capture Mode</span>
                    <select
                        value={form.captureMode}
                        onChange={(event) => onChange({ captureMode: event.target.value as DocumentScanCaptureMode })}
                        className={styles.select}
                        disabled={!editable}
                        data-test-id="document-type-detail-capture-mode-select"
                    >
                        <option value="unnamed_pages">{formatCaptureMode('unnamed_pages')}</option>
                        <option value="named_slots">{formatCaptureMode('named_slots')}</option>
                    </select>
                </label>
                {form.captureMode === 'unnamed_pages' ? (
                    <div className={styles.tableBlock}>
                        <div className={styles.compactHeader}>
                            <div>
                                <span className={styles.fieldLabel}>Image Count</span>
                                <p className={styles.helpText}>
                                    Capture one or more document page images. Use min/max images to control single-page or
                                    multi-page capture.
                                </p>
                            </div>
                        </div>
                        <div className={styles.inlineFieldGrid}>
                            <CompactInput
                                label="Minimum Images"
                                type="number"
                                value={getPageCount(form, 'minImages')}
                                onChangeText={(value: string) => updatePageCount('minImages', value)}
                                disabled={!editable}
                                data-test-id="document-type-min-images-input"
                            />
                            <CompactInput
                                label="Maximum Images"
                                type="number"
                                value={getPageCount(form, 'maxImages')}
                                onChangeText={(value: string) => updatePageCount('maxImages', value)}
                                disabled={!editable}
                                data-test-id="document-type-max-images-input"
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.slotEditor} data-test-id="document-type-slot-editor">
                        <Flex direction="row" align="center" justify="between" className={styles.compactHeader}>
                            <div>
                                <span className={styles.fieldLabel}>Image Labels</span>
                                <p className={styles.helpText}>
                                    Capture specific labeled images, such as Front, Back, or Signature Page.
                                </p>
                            </div>
                            {editable ? (
                                <Button
                                    type="outline"
                                    text="Add Label"
                                    onClick={addSlot}
                                    data-test-id="add-document-type-slot-button"
                                />
                            ) : null}
                        </Flex>
                        {slots.length === 0 ? <div className={styles.quietState}>No image labels configured.</div> : null}
                        {slots.length > 0 ? (
                            <div className={styles.slotColumnHeader} aria-hidden="true">
                                <span>Label</span>
                                <span>Key</span>
                                <span>Required</span>
                                {editable ? <span /> : null}
                            </div>
                        ) : null}
                        {slots.map((slot, index) => (
                            <div key={`slot-row-${index}`} className={styles.slotRow}>
                                <CompactInput
                                    label="Label"
                                    hideLabel={true}
                                    value={slot.label}
                                    onChangeText={(label: string) => updateSlotLabel(index, label)}
                                    disabled={!editable}
                                    placeholder="Front"
                                    data-test-id={`document-type-slot-label-${index}`}
                                />
                                <CompactInput
                                    label="Key"
                                    hideLabel={true}
                                    value={slot.slotKey}
                                    onChangeText={(slotKey: string) => updateSlotKey(index, slotKey)}
                                    disabled={!editable}
                                    placeholder="front"
                                    data-test-id={`document-type-slot-key-${index}`}
                                />
                                <label className={classNames(styles.checkboxRow, styles.slotRequiredCheckbox)}>
                                    <input
                                        type="checkbox"
                                        checked={slot.required}
                                        onChange={(event) => updateSlot(index, { required: event.target.checked })}
                                        disabled={!editable}
                                        data-test-id={`document-type-slot-required-${index}`}
                                    />
                                    Required
                                </label>
                                {editable ? (
                                    <Button
                                        type="transparent"
                                        text="Remove"
                                        onClick={() => removeSlot(index)}
                                        className={styles.removeButton}
                                        data-test-id={`remove-document-type-slot-${index}`}
                                    />
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
                <div className={styles.tableBlock}>
                    <div className={styles.compactHeader}>
                        <span className={styles.fieldLabel}>Native Crop Detection</span>
                        <p className={styles.helpText}>
                            Use iOS document boundary detection before saving captured images.
                        </p>
                    </div>
                    <div className={styles.inlineFieldGrid}>
                        <label className={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                checked={nativeDocumentCropEnabled}
                                onChange={(event) =>
                                    onChange((current) => setNativeDocumentCropConfig(current, event.target.checked))
                                }
                                disabled={!editable}
                                data-test-id="document-type-native-crop-checkbox"
                            />
                            Enable native document detection
                        </label>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>Detection Profile</span>
                            <select
                                value={nativeDocumentCropProfile}
                                onChange={(event) => {
                                    const profile = isNativeDocumentCropProfile(event.target.value)
                                        ? event.target.value
                                        : 'document';
                                    onChange((current) => setNativeDocumentCropConfig(current, true, profile));
                                }}
                                className={styles.select}
                                disabled={!editable || !nativeDocumentCropEnabled}
                                data-test-id="document-type-native-crop-profile-select"
                            >
                                <option value="document">Document</option>
                                <option value="card">Card</option>
                            </select>
                        </label>
                    </div>
                </div>
            </div>
        </SectionShell>
    );
}

function ExtractionSection({
    form,
    editable,
    onChange,
}: {
    form: DraftFormState;
    editable: boolean;
    onChange: (updates: Partial<DraftFormState> | ((current: DraftFormState) => DraftFormState)) => void;
}) {
    const [activeExtractionTab, setActiveExtractionTab] = useState<'prompt' | 'schema'>('prompt');

    return (
        <SectionShell title="Extraction" description="Tell Capture AI what to extract and how to shape the output.">
            <div className={styles.formGrid}>
                <div className={styles.segmentedControl} data-test-id="document-type-extraction-subtabs">
                    <button
                        className={activeExtractionTab === 'prompt' ? styles.segmentActive : styles.segment}
                        onClick={() => setActiveExtractionTab('prompt')}
                        data-test-id="document-type-extraction-prompt-tab"
                    >
                        Prompt
                    </button>
                    <button
                        className={activeExtractionTab === 'schema' ? styles.segmentActive : styles.segment}
                        onClick={() => setActiveExtractionTab('schema')}
                        data-test-id="document-type-extraction-schema-tab"
                    >
                        Extraction Schema
                    </button>
                </div>
                {activeExtractionTab === 'prompt' ? (
                    <CompactInput
                        label="Prompt"
                        value={form.promptText}
                        onChangeText={(promptText: string) => onChange({ promptText })}
                        multiLine={true}
                        rows={22}
                        required={true}
                        disabled={!editable}
                        className={styles.extractionEditor}
                        data-test-id="document-type-detail-prompt-input"
                    />
                ) : (
                    <CompactInput
                        label="JSON Schema"
                        value={form.extractionSchemaText}
                        onChangeText={(extractionSchemaText: string) => onChange({ extractionSchemaText })}
                        multiLine={true}
                        rows={28}
                        disabled={!editable}
                        className={classNames(styles.codeEditor, styles.extractionSchemaEditor)}
                        data-test-id="document-type-detail-extraction-schema-input"
                    />
                )}
            </div>
        </SectionShell>
    );
}

function MappingSection({
    scanType,
    form,
    editable,
    onChange,
}: {
    scanType: DocumentScanTypeAdminSummary;
    form: DraftFormState;
    editable: boolean;
    onChange: (updates: Partial<DraftFormState> | ((current: DraftFormState) => DraftFormState)) => void;
}) {
    const [activeMappingTab, setActiveMappingTab] = useState<'settings' | 'schema'>('settings');
    const mappingTitle = getEntityMappingTitle(scanType.supportedEntityType);
    const schemaLabel = getEntityMappingSchemaLabel(scanType.supportedEntityType);
    const entityBehaviorCopy = getEntityBehaviorCopy(scanType.supportedEntityType);
    const extractionSchema = safeParseJsonRecord(form.extractionSchemaText || '{}');
    const availableTokens = getExtractionSchemaFieldNames(extractionSchema);
    const encounterNumberTemplate = getEncounterNumberTemplate(form);
    const templateErrors = getEncounterNumberTemplateErrors(form, scanType.supportedEntityType);
    const validationErrors = getEntityMappingSchemaErrors(
        form.entityMappingSchemaText,
        scanType.supportedEntityType,
        form.allowEntityCreation,
    );

    return (
        <SectionShell title={mappingTitle} description={getEntityMappingDescription(scanType.supportedEntityType)}>
            <div className={styles.formGrid}>
                <div className={styles.segmentedControl} data-test-id="document-type-mapping-subtabs">
                    <button
                        className={activeMappingTab === 'settings' ? styles.segmentActive : styles.segment}
                        onClick={() => setActiveMappingTab('settings')}
                        data-test-id="document-type-mapping-settings-tab"
                    >
                        {getEntityMappingSettingsTabLabel(scanType.supportedEntityType)}
                    </button>
                    <button
                        className={activeMappingTab === 'schema' ? styles.segmentActive : styles.segment}
                        onClick={() => setActiveMappingTab('schema')}
                        data-test-id="document-type-mapping-schema-tab"
                    >
                        {schemaLabel}
                    </button>
                </div>
                {activeMappingTab === 'settings' ? (
                    <div className={styles.toggleGrid}>
                        <label className={styles.toggleRow}>
                            <input
                                type="checkbox"
                                checked={form.allowEntityCreation}
                                onChange={(event) =>
                                    onChange((current) => {
                                        if (!event.target.checked) {
                                            return { ...current, allowEntityCreation: false };
                                        }
                                        const config = safeParseJsonRecord(current.entityCreationConfigText || '{}');
                                        return {
                                            ...current,
                                            allowEntityCreation: true,
                                            entityCreationConfigText: toJsonText(
                                                {
                                                    ...config,
                                                    preserveLegacyDuplicateEncounterBehavior: true,
                                                },
                                                null,
                                            ),
                                        };
                                    })
                                }
                                disabled={!editable}
                                data-test-id="document-type-detail-allow-create-checkbox"
                            />
                            <span>
                                <strong>{entityBehaviorCopy.createLabel}</strong>
                                <small>{entityBehaviorCopy.createDescription}</small>
                            </span>
                        </label>
                        <label className={styles.toggleRow}>
                            <input
                                type="checkbox"
                                checked={form.updateLinkedEntity}
                                onChange={(event) => onChange({ updateLinkedEntity: event.target.checked })}
                                disabled={!editable}
                                data-test-id="document-type-detail-update-linked-checkbox"
                            />
                            <span>
                                <strong>{entityBehaviorCopy.updateLabel}</strong>
                                <small>{entityBehaviorCopy.updateDescription}</small>
                            </span>
                        </label>
                        {scanType.supportedEntityType === 'encounter' ? (
                            <div className={styles.tableBlock}>
                                <CompactInput
                                    label="Encounter Number Template"
                                    value={encounterNumberTemplate}
                                    onChangeText={(value: string) =>
                                        onChange((current) => setEncounterNumberTemplate(current, value))
                                    }
                                    disabled={!editable || !form.allowEntityCreation}
                                    placeholder="{{patFrstNm|initial}}{{patLastNm|initial}}-{{patDob|yyyyMMdd}}"
                                    data-test-id="document-type-detail-encounter-number-template-input"
                                />
                                <p className={styles.helpText}>
                                    Available tokens use extraction schema fields. Modifiers:{' '}
                                    {EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_MODIFIERS.join(', ')}. Reserved constants:
                                    {` {{@now|yyyyMMdd}}, {{@now|HHmmss}}`}.
                                </p>
                                {availableTokens.length ? (
                                    <ul
                                        className={styles.previewList}
                                        data-test-id="document-type-encounter-number-template-token-list"
                                    >
                                        {availableTokens.map((fieldName) => (
                                            <li key={fieldName}>{`{{${fieldName}}}`}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className={styles.quietState}>No extraction schema fields are available.</div>
                                )}
                                {templateErrors.length ? (
                                    <div
                                        className={styles.resultBanner}
                                        data-test-id="document-type-encounter-number-template-error"
                                    >
                                        {templateErrors.join('; ')}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <>
                        <CompactInput
                            label={schemaLabel}
                            value={form.entityMappingSchemaText}
                            onChangeText={(entityMappingSchemaText: string) => onChange({ entityMappingSchemaText })}
                            multiLine={true}
                            rows={28}
                            disabled={!editable}
                            className={classNames(styles.codeEditor, styles.extractionSchemaEditor)}
                            data-test-id="document-type-detail-entity-mapping-schema-input"
                        />
                        <p className={styles.helpText}>
                            {getEntityMappingSchemaHelpText(scanType.supportedEntityType)}
                        </p>
                        {validationErrors.length ? (
                            <div
                                className={styles.resultBanner}
                                data-test-id="document-type-detail-entity-mapping-schema-error"
                            >
                                {validationErrors.join('; ')}
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </SectionShell>
    );
}

function TestResultPanel({
    result,
    form,
}: {
    result: DocumentScanTypeTestResult | null;
    form: DraftFormState | null;
}) {
    const fields = getExtractionFields(form);
    const entityFields = getEntityMappingFieldNames(form);

    if (!result) {
        return (
            <div className={styles.quietState} data-test-id="document-type-test-empty-state">
                Run a test to confirm the current configuration validates before publishing.
            </div>
        );
    }

    return (
        <div className={styles.testResultPanel} data-test-id="document-type-test-result">
            <div className={styles.testResultHeader}>Test status: {formatValue(result.status)}</div>
            <div className={styles.testSteps}>
                {result.steps.map((step) => (
                    <StatusChip key={`${step.step}-${step.status}`} text={`${formatValue(step.step)}: ${formatValue(step.status)}`} />
                ))}
            </div>
            {result.validationErrors.length > 0 ? (
                <div className={styles.resultBanner}>
                    <strong>Validation errors:</strong> {result.validationErrors.join('; ')}
                </div>
            ) : null}
            <div className={styles.previewColumns}>
                <div>
                    <span className={styles.fieldLabel}>Configured Extracted Fields</span>
                    {fields.length ? (
                        <ul className={styles.previewList}>
                            {fields.map((field) => (
                                <li key={field.fieldKey}>{field.fieldKey}</li>
                            ))}
                        </ul>
                    ) : (
                        <div className={styles.quietState}>No extracted fields configured.</div>
                    )}
                </div>
                <div>
                    <span className={styles.fieldLabel}>Entity Fields</span>
                    {entityFields.length ? (
                        <ul className={styles.previewList}>
                            {entityFields.map((fieldName) => (
                                <li key={fieldName}>{fieldName}</li>
                            ))}
                        </ul>
                    ) : (
                        <div className={styles.quietState}>No entity fields configured.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TestModal({
    isOpen,
    onClose,
    targetVersion,
    availability,
    testImageName,
    isTesting,
    testResult,
    form,
    currentFacilityId,
    onTestImageChange,
    onRunTest,
}: {
    isOpen: boolean;
    onClose: () => void;
    targetVersion: DocumentScanTypeVersionSummary | null;
    availability?: DocumentScanTypeFacilityAvailabilitySummary;
    testImageName: string;
    isTesting: boolean;
    testResult: DocumentScanTypeTestResult | null;
    form: DraftFormState | null;
    currentFacilityId?: number | null;
    onTestImageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRunTest: (facilityId: number | null) => void;
}) {
    const [facilityId, setFacilityId] = useState<number | null>(currentFacilityId ?? null);

    useEffect(() => {
        if (isOpen) {
            setFacilityId(currentFacilityId ?? null);
        }
    }, [currentFacilityId, isOpen]);

    return (
        <SideModal isOpen={isOpen} isWide={true} onClose={onClose}>
            <ModalHeader title="Test Document Type" onClose={onClose} />
            <div className={styles.overrideModalBody} data-test-id="document-type-test-modal">
                <div className={styles.quietState}>
                    Testing validates the selected {targetVersion?.versionState || 'version'} configuration. Select a
                    facility to apply its current draft override when one exists.
                </div>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Facility Context</span>
                    <select
                        value={facilityId || ''}
                        onChange={(event) => setFacilityId(event.target.value ? Number(event.target.value) : null)}
                        className={styles.select}
                        data-test-id="document-type-test-facility-select"
                    >
                        <option value="">Org defaults</option>
                        {(availability?.facilities || []).map((facility) => (
                            <option key={facility.facilityId} value={facility.facilityId}>
                                {facility.facilityName || `Facility ${facility.facilityId}`}
                            </option>
                        ))}
                    </select>
                </label>
                <div className={styles.testUploader}>
                    <span className={styles.fieldLabel}>Single Image Test</span>
                    <Flex direction="row" align="center" className={styles.filePickerRow}>
                        <label className={styles.fileButton}>
                            Choose Image
                            <input
                                type="file"
                                accept="image/*"
                                onChange={onTestImageChange}
                                disabled={!targetVersion}
                                data-test-id="document-type-test-image-input"
                            />
                        </label>
                        <span className={styles.fileName}>{testImageName || 'No image selected'}</span>
                        <Button
                            type="primary"
                            text="Run Test"
                            onClick={() => onRunTest(facilityId)}
                            isLoading={isTesting}
                            disabled={!targetVersion}
                            data-test-id="run-document-type-test-button"
                        />
                    </Flex>
                </div>
                <TestResultPanel result={testResult} form={form} />
            </div>
        </SideModal>
    );
}

function buildOverrideForm(
    facility: FacilityDocumentScanTypeAvailabilitySummary,
    baseForm: DraftFormState | null,
    selectedVersion?: FacilityDocumentScanTypeAvailabilitySummary['draftVersion'] | FacilityDocumentScanTypeAvailabilitySummary['publishedVersion'] | null,
): FacilityOverrideFormState {
    const version = selectedVersion === undefined ? facility.draftVersion || facility.publishedVersion : selectedVersion;
    const overrides = (version?.overrides || {}) as Record<string, unknown>;
    const baseExtractionSchema = safeParseJsonRecord(baseForm?.extractionSchemaText || '{}');
    const baseMapping = safeParseJsonRecord(baseForm?.entityMappingSchemaText || '{}');
    const baseEntityCreationConfig = safeParseJsonRecord(baseForm?.entityCreationConfigText || '{}');
    const promptTextMode = overrides.promptTextMode === 'append' ? 'append' : 'replace';
    const entityCreationConfigValue = hasOwn(overrides, 'entityCreationConfig')
        ? overrides.entityCreationConfig
        : baseEntityCreationConfig;

    return {
        sectionEnabled: {
            prompt: hasOwn(overrides, 'promptText') || hasOwn(overrides, 'promptTextMode'),
            extraction: hasOwn(overrides, 'extractionSchema'),
            mapping: hasOwn(overrides, 'entityMappingSchema') || hasOwn(overrides, 'entityCreationConfig'),
        },
        promptTextMode,
        promptText: `${overrides.promptText ?? baseForm?.promptText ?? ''}`,
        extractionSchemaText: toJsonText(overrides.extractionSchema ?? baseExtractionSchema, {}),
        entityMappingSchemaText: toJsonText(overrides.entityMappingSchema ?? baseMapping, {}),
        entityCreationConfigText: toJsonText(entityCreationConfigValue, null),
    };
}

function buildOverridePayload(
    form: FacilityOverrideFormState,
    supportedEntityType: DocumentScanSupportedEntityType,
    allowEntityCreation: boolean,
) {
    const overrides: Record<string, unknown> = {};
    const extractionSchema = form.sectionEnabled.extraction
        ? parseJsonRecord(form.extractionSchemaText, 'Extraction override schema', true)
        : parseJsonRecord(form.extractionSchemaText, 'Extraction schema', true);
    if (form.sectionEnabled.prompt) {
        overrides.promptText = form.promptText;
        if (form.promptTextMode === 'append') {
            overrides.promptTextMode = form.promptTextMode;
        }
    }
    if (form.sectionEnabled.extraction) {
        overrides.extractionSchema = extractionSchema;
    }
    if (form.sectionEnabled.mapping) {
        overrides.entityMappingSchema = parseEntityMappingSchemaText(
            form.entityMappingSchemaText,
            `${getEntityMappingSchemaLabel(supportedEntityType)} override`,
            supportedEntityType,
            allowEntityCreation,
        );
        overrides.entityCreationConfig = parseEntityCreationConfigText(
            form.entityCreationConfigText,
            extractionSchema,
            supportedEntityType,
        );
    }
    return overrides;
}

function getPromptTextForModeChange(
    currentPromptText: string,
    promptTextMode: 'replace' | 'append',
    basePromptText: string,
) {
    if (promptTextMode === 'append' && currentPromptText === basePromptText) {
        return '';
    }
    if (promptTextMode === 'replace' && currentPromptText.trim() === '') {
        return basePromptText;
    }
    return currentPromptText;
}

function FacilityExtractionOverrideSection({
    form,
    basePromptText,
    editable,
    onChange,
}: {
    form: FacilityOverrideFormState;
    basePromptText: string;
    editable: boolean;
    onChange: (updates: FacilityOverrideFormUpdate) => void;
}) {
    const [activeExtractionTab, setActiveExtractionTab] = useState<'prompt' | 'schema'>('prompt');
    const [orgDefaultPromptExpanded, setOrgDefaultPromptExpanded] = useState(true);
    const enabled = form.sectionEnabled.prompt || form.sectionEnabled.extraction;
    const toggleExtractionOverride = (checked: boolean) => {
        onChange((current) => ({
            ...current,
            sectionEnabled: {
                ...current.sectionEnabled,
                prompt: checked,
                extraction: checked,
            },
        }));
    };
    const changePromptTextMode = (promptTextMode: 'replace' | 'append') => {
        onChange((current) => ({
            ...current,
            promptTextMode,
            promptText: getPromptTextForModeChange(current.promptText, promptTextMode, basePromptText),
        }));
    };

    return (
        <SectionShell title="Extraction" description="Tell Capture AI what to extract and how to shape the output.">
            <OverrideSectionEditor
                title="Override extraction for this facility"
                enabled={enabled}
                disabled={!editable}
                onToggle={toggleExtractionOverride}
                data-test-id="facility-override-extraction-checkbox"
            >
                <div className={styles.formGrid}>
                    <div className={styles.segmentedControl} data-test-id="facility-override-extraction-subtabs">
                        <button
                            className={activeExtractionTab === 'prompt' ? styles.segmentActive : styles.segment}
                            onClick={() => setActiveExtractionTab('prompt')}
                            data-test-id="facility-override-extraction-prompt-tab"
                        >
                            Prompt
                        </button>
                        <button
                            className={activeExtractionTab === 'schema' ? styles.segmentActive : styles.segment}
                            onClick={() => setActiveExtractionTab('schema')}
                            data-test-id="facility-override-extraction-schema-tab"
                        >
                            Extraction Schema
                        </button>
                    </div>
                    {activeExtractionTab === 'prompt' ? (
                        <>
                            <label className={styles.field} data-test-id="facility-override-prompt-mode">
                                <span className={styles.fieldLabel}>Override Mode</span>
                                <select
                                    value={form.promptTextMode}
                                    onChange={(event) =>
                                        changePromptTextMode(
                                            event.target.value === 'append' ? 'append' : 'replace',
                                        )
                                    }
                                    className={styles.select}
                                    disabled={!editable || !enabled}
                                    data-test-id="facility-override-prompt-mode-select"
                                >
                                    <option value="append">Append</option>
                                    <option value="replace">Override</option>
                                </select>
                            </label>
                            {form.promptTextMode === 'append' ? (
                                <div
                                    className={styles.orgDefaultPromptPreview}
                                    data-test-id="facility-override-org-default-prompt"
                                >
                                    <button
                                        type="button"
                                        className={styles.orgDefaultPromptToggle}
                                        onClick={() => setOrgDefaultPromptExpanded((current) => !current)}
                                        data-test-id="facility-override-org-default-prompt-toggle"
                                    >
                                        <span>Org Default Prompt</span>
                                        <span>{orgDefaultPromptExpanded ? 'Hide' : 'Show'}</span>
                                    </button>
                                    {orgDefaultPromptExpanded ? (
                                        <pre
                                            className={styles.orgDefaultPromptText}
                                            data-test-id="facility-override-org-default-prompt-text"
                                        >
                                            {basePromptText || 'No org default prompt configured.'}
                                        </pre>
                                    ) : null}
                                </div>
                            ) : null}
                            <CompactInput
                                label={form.promptTextMode === 'append' ? 'Prompt Addendum' : 'Prompt'}
                                value={form.promptText}
                                onChangeText={(promptText: string) => onChange({ promptText })}
                                multiLine={true}
                                rows={22}
                                required={true}
                                disabled={!editable || !enabled}
                                className={styles.extractionEditor}
                                data-test-id="facility-override-prompt-input"
                            />
                        </>
                    ) : (
                        <CompactInput
                            label="JSON Schema"
                            value={form.extractionSchemaText}
                            onChangeText={(extractionSchemaText: string) => onChange({ extractionSchemaText })}
                            multiLine={true}
                            rows={28}
                            disabled={!editable || !enabled}
                            className={classNames(styles.codeEditor, styles.extractionSchemaEditor)}
                            data-test-id="facility-override-extraction-schema-input"
                        />
                    )}
                </div>
            </OverrideSectionEditor>
        </SectionShell>
    );
}

function FacilityMappingOverrideSection({
    scanType,
    form,
    allowEntityCreation,
    editable,
    onChange,
}: {
    scanType: DocumentScanTypeAdminSummary;
    form: FacilityOverrideFormState;
    allowEntityCreation: boolean;
    editable: boolean;
    onChange: (updates: FacilityOverrideFormUpdate) => void;
}) {
    const enabled = form.sectionEnabled.mapping;
    const mappingEditable = editable && enabled;
    const mappingTitle = getEntityMappingTitle(scanType.supportedEntityType);
    const schemaLabel = getEntityMappingSchemaLabel(scanType.supportedEntityType);
    const availableTokens = getExtractionSchemaFieldNames(safeParseJsonRecord(form.extractionSchemaText || '{}'));
    const encounterNumberTemplate = getEncounterNumberTemplate({
        ...form,
        allowEntityCreation,
    });
    const validationErrors = enabled
        ? getEntityMappingSchemaErrors(form.entityMappingSchemaText, scanType.supportedEntityType, allowEntityCreation)
        : [];
    const templateErrors =
        enabled && scanType.supportedEntityType === 'encounter'
            ? getEncounterNumberTemplateErrors(
                  {
                      ...form,
                      allowEntityCreation,
                  },
                  scanType.supportedEntityType,
              )
            : [];

    return (
        <SectionShell
            title={mappingTitle}
            description={getEntityMappingOverrideDescription(scanType.supportedEntityType)}
        >
            <OverrideSectionEditor
                title={`Override ${schemaLabel.toLowerCase()} for this facility`}
                enabled={enabled}
                disabled={!editable}
                onToggle={(checked) =>
                    onChange((current) => ({
                        ...current,
                        sectionEnabled: { ...current.sectionEnabled, mapping: checked },
                    }))
                }
                data-test-id="facility-override-mapping-checkbox"
            >
                <CompactInput
                    label={schemaLabel}
                    value={form.entityMappingSchemaText}
                    onChangeText={(entityMappingSchemaText: string) => onChange({ entityMappingSchemaText })}
                    multiLine={true}
                    rows={28}
                    disabled={!mappingEditable}
                    className={classNames(styles.codeEditor, styles.extractionSchemaEditor)}
                    data-test-id="facility-override-entity-mapping-schema-input"
                />
                <p className={styles.helpText}>
                    Facility overrides replace the org {schemaLabel.toLowerCase()} for this facility.
                </p>
                {validationErrors.length ? (
                    <div className={styles.resultBanner} data-test-id="facility-override-entity-mapping-schema-error">
                        {validationErrors.join('; ')}
                    </div>
                ) : null}
                {scanType.supportedEntityType === 'encounter' ? (
                    <div className={styles.tableBlock}>
                        <CompactInput
                            label="Encounter Number Template"
                            value={encounterNumberTemplate}
                            onChangeText={(value: string) =>
                                onChange((current) =>
                                    setEncounterNumberTemplate(
                                        {
                                            ...current,
                                            allowEntityCreation,
                                        },
                                        value,
                                    ),
                                )
                            }
                            disabled={!mappingEditable || !allowEntityCreation}
                            placeholder="{{patFrstNm|initial}}{{patLastNm|initial}}-{{patDob|yyyyMMdd}}"
                            data-test-id="facility-override-encounter-number-template-input"
                        />
                        <p className={styles.helpText}>
                            Available tokens use extraction schema fields. Modifiers:{' '}
                            {EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_MODIFIERS.join(', ')}. Reserved constants:
                            {` {{@now|yyyyMMdd}}, {{@now|HHmmss}}`}.
                        </p>
                        {availableTokens.length ? (
                            <ul className={styles.previewList}>
                                {availableTokens.map((fieldName) => (
                                    <li key={fieldName}>{`{{${fieldName}}}`}</li>
                                ))}
                            </ul>
                        ) : (
                            <div className={styles.quietState}>No extraction schema fields are available.</div>
                        )}
                        {templateErrors.length ? (
                            <div
                                className={styles.resultBanner}
                                data-test-id="facility-override-encounter-number-template-error"
                            >
                                {templateErrors.join('; ')}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </OverrideSectionEditor>
        </SectionShell>
    );
}

function OverrideSectionEditor({
    title,
    enabled,
    disabled,
    onToggle,
    children,
    'data-test-id': dataTestId,
}: {
    title: string;
    enabled: boolean;
    disabled?: boolean;
    onToggle: (enabled: boolean) => void;
    children: React.ReactNode;
    'data-test-id'?: string;
}) {
    return (
        <div className={styles.overrideSection}>
            <Flex direction="row" align="center" justify="between" className={styles.overrideSectionHeader}>
                <div>
                    <strong>{title}</strong>
                    <span>{enabled ? 'Overridden for this facility' : 'Uses org defaults'}</span>
                </div>
                <label className={styles.switchLabel}>
                    <input
                        type="checkbox"
                        checked={enabled}
                        disabled={disabled}
                        onChange={(event) => onToggle(event.target.checked)}
                        data-test-id={dataTestId}
                    />
                    I want to override this section
                </label>
            </Flex>
            {children}
        </div>
    );
}

function DocumentTypeSettingsModal({
    isOpen,
    onClose,
    scanType,
    documentScanTypeId,
    onSaved,
}: {
    isOpen: boolean;
    onClose: () => void;
    scanType: DocumentScanTypeAdminSummary;
    documentScanTypeId: string;
    onSaved: () => void;
}) {
    const {
        data: availability,
        isLoading,
        error,
        refetch,
    } = useDocumentScanTypeFacilityAvailability(documentScanTypeId) as {
        data?: DocumentScanTypeFacilityAvailabilitySummary;
        isLoading: boolean;
        error: unknown;
        refetch: () => void;
    };
    const { mutateAsync: updateSettings, isLoading: isSavingSettings } = useUpdateDocumentScanTypeSettings();
    const { mutateAsync: updateAvailability, isLoading: isSavingAvailability } =
        useUpdateDocumentScanTypeFacilityAvailability();
    const { mutateAsync: enableFacilityDocumentScanTypes, isLoading: isEnablingFacilityDocumentScanTypes } =
        useEnableFacilityDocumentScanTypes();
    const [mode, setMode] = useState<FacilityAvailabilityMode>('selected_facilities');
    const [selectedFacilityIds, setSelectedFacilityIds] = useState<number[]>([]);
    const [settingsName, setSettingsName] = useState(scanType.displayName || '');
    const [settingsDescription, setSettingsDescription] = useState(scanType.description || '');
    const [checkingFacilityEnablementId, setCheckingFacilityEnablementId] = useState<number | null>(null);
    const [facilityEnablementConfirmation, setFacilityEnablementConfirmation] =
        useState<FacilityEnablementConfirmation | null>(null);
    const isEncounterFormScanType = scanType.supportedEntityType === 'encounter_form';

    useEffect(() => {
        if (!availability) {
            return;
        }
        setMode(availability.mode);
        setSelectedFacilityIds(availability.facilityIds);
    }, [availability]);

    useEffect(() => {
        setSettingsName(scanType.displayName || '');
        setSettingsDescription(scanType.description || '');
    }, [scanType.description, scanType.displayName]);

    const saveAvailability = async (nextMode: FacilityAvailabilityMode, nextFacilityIds: number[]) => {
        try {
            await updateAvailability({
                documentScanTypeId,
                mode: nextMode,
                facilityIds: nextMode === 'all_facilities' ? [] : nextFacilityIds,
            });
            ToastStoreObject.show('Facility availability saved');
            onSaved();
            refetch();
        } catch (availabilityError) {
            ToastStoreObject.show(getErrorMessage(availabilityError));
        }
    };

    const changeAvailabilityMode = async (nextMode: FacilityAvailabilityMode) => {
        setMode(nextMode);
        await saveAvailability(nextMode, selectedFacilityIds);
    };

    const toggleSelectedFacility = async (facilityId: number, checked: boolean) => {
        const nextFacilityIds = checked
            ? Array.from(new Set([...selectedFacilityIds, facilityId]))
            : selectedFacilityIds.filter((id) => id !== facilityId);
        setSelectedFacilityIds(nextFacilityIds);
        await saveAvailability(mode, nextFacilityIds);
    };

    const openFacilityEnablementConfirmation = async (facility: FacilityDocumentScanTypeAvailabilitySummary) => {
        setCheckingFacilityEnablementId(facility.facilityId);
        try {
            const legacyCaptureAiOption = await facilitiesApi.fetchFacilityOption(
                facility.facilityId,
                LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME,
            );
            setFacilityEnablementConfirmation({
                facility,
                disableLegacyCaptureAi: legacyCaptureAiOption?.optVal?.toLowerCase() === 'true',
            });
        } catch (enablementError) {
            ToastStoreObject.show(getErrorMessage(enablementError));
        } finally {
            setCheckingFacilityEnablementId(null);
        }
    };

    const confirmFacilityEnablement = async () => {
        if (!facilityEnablementConfirmation) {
            return;
        }

        const { facility, disableLegacyCaptureAi } = facilityEnablementConfirmation;
        try {
            await enableFacilityDocumentScanTypes({
                documentScanTypeId,
                facilityId: facility.facilityId,
                disableLegacyCaptureAi,
            });
            ToastStoreObject.show(`${getFacilityLabel(facility)} enabled for Capture AI document types`);
            setFacilityEnablementConfirmation(null);
            refetch();
        } catch (enablementError) {
            ToastStoreObject.show(getErrorMessage(enablementError));
        }
    };

    const handleSaveSettings = async () => {
        try {
            await updateSettings({
                documentScanTypeId,
                data: {
                    name: settingsName.trim(),
                    description: settingsDescription.trim() || null,
                },
            });
            ToastStoreObject.show('Document type settings saved');
            onSaved();
        } catch (settingsError) {
            ToastStoreObject.show(getErrorMessage(settingsError));
        }
    };

    const settingsFacilities = useMemo(
        () =>
            (availability?.facilities || []).map((facility) => ({
                ...facility,
                availabilityEnabled: getFacilityStatus(mode, selectedFacilityIds, facility).selected,
            })),
        [availability?.facilities, mode, selectedFacilityIds],
    );

    return (
        <SideModal isOpen={isOpen} isWide={true} onClose={onClose}>
            <ModalHeader title="Document Type Settings" onClose={onClose} />
            <div className={styles.overrideModalBody} data-test-id="document-type-settings-modal">
                <div className={styles.quietState}>
                    Facility settings control where this document type is available. These settings are saved separately
                    from the document type draft.
                </div>
                <div className={styles.formGrid}>
                    <CompactInput
                        label="Display Name"
                        value={settingsName}
                        onChangeText={(name: string) => setSettingsName(name)}
                        required={true}
                        data-test-id="document-type-settings-name-input"
                    />
                    <CompactInput
                        label="Description"
                        value={settingsDescription}
                        onChangeText={(description: string) => setSettingsDescription(description)}
                        multiLine={true}
                        rows={3}
                        data-test-id="document-type-settings-description-input"
                    />
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>Supported Entity</span>
                        <select
                            value={scanType.supportedEntityType}
                            className={styles.select}
                            disabled={true}
                            data-test-id="document-type-settings-supported-entity-select"
                        >
                            <option value="encounter">Encounter</option>
                            <option value="encounter_form">Encounter Form</option>
                        </select>
                    </label>
                </div>
                {error && (
                    <Card className={styles.errorCard} data-test-id="facility-availability-error-card">
                        <div className={styles.errorMessage}>
                            <strong>Error loading facility availability:</strong> {getErrorMessage(error)}
                            <Button
                                type="small"
                                text="Retry"
                                onClick={() => refetch()}
                                className={styles.retryButton}
                                data-test-id="facility-availability-retry-button"
                            />
                        </div>
                    </Card>
                )}
                {isLoading || !availability ? (
                    <div className={styles.emptyState}>Loading facility availability...</div>
                ) : (
                    <div className={styles.formGrid}>
                        <Flex direction="row" align="center" justify="between">
                            <label className={styles.availabilityControl}>
                                <input
                                    type="checkbox"
                                    checked={mode === 'all_facilities'}
                                    disabled={isSavingAvailability}
                                    onChange={(event) =>
                                        changeAvailabilityMode(
                                            event.target.checked ? 'all_facilities' : 'selected_facilities',
                                        )
                                    }
                                    data-test-id="availability-all-facilities-checkbox"
                                />
                                <span>
                                    <strong>Enable this document type at all facilities</strong>
                                    <small>
                                        This applies only to facilities where Document Types is enabled in Facility
                                        Settings. When unchecked, only selected enabled facilities can use this document
                                        type.
                                    </small>
                                </span>
                            </label>
                        </Flex>
                        <div className={styles.facilityTable} data-test-id="facility-availability-table">
                            <div className={styles.facilityTableHeader}>
                                <span>Facility</span>
                                <span>Availability</span>
                                <span>Last Change</span>
                            </div>
                            {availability.facilities.map((facility) => {
                                const status = getFacilityStatus(mode, selectedFacilityIds, facility);
                                return (
                                    <div
                                        key={facility.facilityId}
                                        className={classNames(styles.facilityTableRow, {
                                            [styles.facilityTableRowDisabled]:
                                                !facility.captureAiScanTypesEnabled,
                                        })}
                                    >
                                        <label className={styles.checkboxRow}>
                                            <input
                                                type="checkbox"
                                                checked={status.selected}
                                                disabled={
                                                    mode === 'all_facilities' ||
                                                    isSavingAvailability ||
                                                    !facility.captureAiScanTypesEnabled
                                                }
                                                onChange={(event) =>
                                                    toggleSelectedFacility(facility.facilityId, event.target.checked)
                                                }
                                                data-test-id={`facility-availability-${facility.facilityId}`}
                                            />
                                            {facility.facilityName || `Facility ${facility.facilityId}`}
                                        </label>
                                        <div className={styles.facilityAvailabilityCell}>
                                            {!facility.captureAiScanTypesEnabled ? (
                                                <StatusChip tone="warning">
                                                    <span>{status.availability}</span>
                                                    <button
                                                        type="button"
                                                        className={styles.facilityEnableLink}
                                                        disabled={
                                                            isSavingAvailability ||
                                                            isEnablingFacilityDocumentScanTypes ||
                                                            checkingFacilityEnablementId === facility.facilityId
                                                        }
                                                        onClick={() => openFacilityEnablementConfirmation(facility)}
                                                        data-test-id={`enable-facility-document-types-${facility.facilityId}`}
                                                    >
                                                        Enable
                                                    </button>
                                                </StatusChip>
                                            ) : (
                                                <StatusChip
                                                    text={status.availability}
                                                    tone={status.availability === 'Enabled' ? 'good' : 'neutral'}
                                                />
                                            )}
                                        </div>
                                        <span>{status.lastChange}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {isEncounterFormScanType && availability ? (
                    <AssociatedFormsSection documentScanTypeId={documentScanTypeId} facilities={settingsFacilities} />
                ) : null}
            </div>
            <ModalFooter>
                <Flex direction="row" align="center" justify="end" self="stretch" className={styles.overrideModalFooter}>
                    <Button type="outline" text="Close" onClick={onClose} className={styles.actionButton} />
                    <Button
                        type="primary"
                        text="Save Details"
                        onClick={handleSaveSettings}
                        isLoading={isSavingSettings}
                        data-test-id="save-facility-availability-button"
                    />
                </Flex>
            </ModalFooter>
            {facilityEnablementConfirmation ? (
                <div data-test-id="enable-facility-document-types-confirmation">
                    <ConfirmationModal
                        headerTitle="Enable Facility Document Types?"
                        title={
                            facilityEnablementConfirmation.disableLegacyCaptureAi
                                ? `Enable Document Types for ${getFacilityLabel(
                                      facilityEnablementConfirmation.facility,
                                  )}? Capture AI v1 is currently enabled for this facility, so this will disable Encounter Scans (Capture AI v1) for the facility and enable Capture AI v2 document types.`
                                : `Enable Document Types for ${getFacilityLabel(
                                      facilityEnablementConfirmation.facility,
                                  )}? This will enable Capture AI v2 document types for the facility.`
                        }
                        confirmButtonText="Enable"
                        cancelButtonText="Cancel"
                        isLoading={isEnablingFacilityDocumentScanTypes}
                        onCancel={() => setFacilityEnablementConfirmation(null)}
                        onConfirm={confirmFacilityEnablement}
                    />
                </div>
            ) : null}
        </SideModal>
        );
    }

export default function DocumentTypeDetail({ documentScanTypeId }: DocumentTypeDetailProps) {
    const history = useHistory();
    const { data: scanType, isLoading, error, refetch } = useAdminDocumentScanType(documentScanTypeId);
    const { mutate: createDraft, isLoading: isCreatingDraft } = useCreateDocumentScanTypeDraft();
    const { mutateAsync: updateDraft, isLoading: isSaving } = useUpdateDocumentScanTypeDraft();
    const { mutateAsync: testVersion, isLoading: isTesting } = useTestDocumentScanTypeVersion();
    const { mutateAsync: publishDraft, isLoading: isPublishing } = usePublishDocumentScanTypeDraft();
    const { data: availability, refetch: refetchAvailability } = useDocumentScanTypeFacilityAvailability(documentScanTypeId);
    const { mutateAsync: createOverrideDraft } = useCreateDocumentScanTypeFacilityOverrideDraft();
    const { mutateAsync: updateOverrideDraft } = useUpdateDocumentScanTypeFacilityOverrideDraft();
    const { mutateAsync: publishOverrideDraft } = usePublishDocumentScanTypeFacilityOverrideDraft();
    const { mutateAsync: discardOverrideDraft } = useDiscardDocumentScanTypeFacilityOverrideDraft();
    const [activeSection, setActiveSection] = useState<DetailSection>('overview');
    const [selectedOverrideFacilityId, setSelectedOverrideFacilityId] = useState<number | null>(null);
    const [overrideForm, setOverrideForm] = useState<FacilityOverrideFormState | null>(null);
    const [localOverrideDrafts, setLocalOverrideDrafts] = useState<
        Record<
            number,
            Pick<FacilityDocumentScanTypeAvailabilitySummary, 'facilityDocumentScanTypeId' | 'draftVersion' | 'hasDraft'>
        >
    >({});
    const [isSavingOverride, setIsSavingOverride] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingOverride, setIsEditingOverride] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
    const [scopeSearchTerm, setScopeSearchTerm] = useState('');
    const [clearOverrideConfirmOpen, setClearOverrideConfirmOpen] = useState(false);
    const [createdOverrideDraftForEdit, setCreatedOverrideDraftForEdit] = useState<{
        facilityId: number;
        facilityDocumentScanTypeVersionId: string;
    } | null>(null);
    const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
    const [facilityOverrideHistoryOpen, setFacilityOverrideHistoryOpen] = useState(false);
    const [selectedReadOnlyVersionId, setSelectedReadOnlyVersionId] = useState<string | null>(null);
    const [selectedOverrideVersionId, setSelectedOverrideVersionId] = useState<string | null>(null);
    const [localDraft, setLocalDraft] = useState<DocumentScanTypeVersionSummary | null>(null);
    const [form, setForm] = useState<DraftFormState | null>(null);
    const [testImageBase64, setTestImageBase64] = useState<string | null>(null);
    const [testImageName, setTestImageName] = useState('');
    const [testResult, setTestResult] = useState<DocumentScanTypeTestResult | null>(null);

    const isEncounterFormScanType = scanType?.supportedEntityType === 'encounter_form';
    const draft = localDraft || scanType?.draftVersion || null;
    const versionHistoryOptions = useMemo(() => getVersionHistoryOptions(scanType), [scanType]);
    const selectedReadOnlyVersion =
        versionHistoryOptions.find((version) => version.documentScanTypeVersionId === selectedReadOnlyVersionId) || null;
    const displayVersion = scanType
        ? isEditing
            ? draft
            : selectedReadOnlyVersion || draft || scanType.publishedVersion
        : null;
    const draftVersionId = draft?.documentScanTypeVersionId || '';
    const isViewingDraft =
        !isEditing && !!draft && displayVersion?.documentScanTypeVersionId === draft.documentScanTypeVersionId;
    const isViewingCurrentPublished =
        !isEditing &&
        !!scanType?.publishedVersion &&
        displayVersion?.documentScanTypeVersionId === scanType.publishedVersion.documentScanTypeVersionId;
    const isViewingHistoricalVersion =
        !isEditing &&
        !!selectedReadOnlyVersion &&
        selectedReadOnlyVersion.versionState === 'published' &&
        selectedReadOnlyVersion.documentScanTypeVersionId !== scanType?.publishedVersion?.documentScanTypeVersionId;
    const pageTitle = useMemo(() => {
        return scanType?.displayName || scanType?.scanTypeKey || 'Document Type';
    }, [scanType?.displayName, scanType?.scanTypeKey]);
    const visibleOverrideFacilities = useMemo(() => {
        if (isEncounterFormScanType) {
            return [];
        }
        return (availability?.facilities || []).filter(
            (facility) =>
                facility.availabilityEnabled &&
                (facility.captureAiScanTypesEnabled || facilityHasOverrideState(facility)),
        );
    }, [availability?.facilities, isEncounterFormScanType]);
    const visibleDetailSections = detailSections;
    const filteredScopeFacilities = useMemo(() => {
        const search = scopeSearchTerm.trim().toLowerCase();
        if (!search) {
            return visibleOverrideFacilities;
        }
        return visibleOverrideFacilities.filter((facility) =>
            getFacilityScopeOptionLabel(facility).toLowerCase().includes(search),
        );
    }, [visibleOverrideFacilities, scopeSearchTerm]);
    const selectedOverrideFacility = useMemo(() => {
        const facility =
            visibleOverrideFacilities.find((availabilityFacility) => availabilityFacility.facilityId === selectedOverrideFacilityId) ||
            null;
        if (!facility) {
            return null;
        }
        const localDraft = localOverrideDrafts[facility.facilityId];
        return localDraft ? { ...facility, ...localDraft } : facility;
    }, [visibleOverrideFacilities, localOverrideDrafts, selectedOverrideFacilityId]);
    const isFacilityOverrideScope = selectedOverrideFacilityId !== null && !isEncounterFormScanType;
    const facilityOverrideHistoryOptions = useMemo(
        () => getFacilityOverrideHistoryOptions(selectedOverrideFacility),
        [selectedOverrideFacility],
    );
    const selectedOverrideVersion =
        facilityOverrideHistoryOptions.find(
            (version) => version.facilityDocumentScanTypeVersionId === selectedOverrideVersionId,
        ) || null;

    useEffect(() => {
        if (displayVersion) {
            setForm(buildDraftForm(displayVersion));
            setTestResult(null);
        } else {
            setForm(null);
        }
    }, [displayVersion?.documentScanTypeVersionId, isEditing]);

    useEffect(() => {
        if (!selectedOverrideFacilityId || !availability) {
            return;
        }
        if (!visibleOverrideFacilities.some((facility) => facility.facilityId === selectedOverrideFacilityId)) {
            setSelectedOverrideFacilityId(null);
        }
    }, [availability, visibleOverrideFacilities, selectedOverrideFacilityId]);

    useEffect(() => {
        if (isEncounterFormScanType && selectedOverrideFacilityId !== null) {
            setSelectedOverrideFacilityId(null);
        }
    }, [isEncounterFormScanType, selectedOverrideFacilityId]);

    useEffect(() => {
        if (!visibleDetailSections.some((section) => section.id === activeSection)) {
            setActiveSection('overview');
        }
    }, [activeSection, visibleDetailSections]);

    useEffect(() => {
        if (!selectedOverrideFacility || !form) {
            setOverrideForm(null);
            return;
        }
        setOverrideForm(
            buildOverrideForm(
                selectedOverrideFacility,
                form,
                isEditingOverride || !selectedOverrideVersionId ? undefined : selectedOverrideVersion,
            ),
        );
    }, [
        isEditingOverride,
        selectedOverrideFacility?.facilityId,
        selectedOverrideFacility?.draftVersion?.facilityDocumentScanTypeVersionId,
        selectedOverrideFacility?.publishedVersion?.facilityDocumentScanTypeVersionId,
        selectedOverrideVersion?.facilityDocumentScanTypeVersionId,
        form?.promptText,
        form?.extractionSchemaText,
        form?.entityMappingSchemaText,
        form?.entityCreationConfigText,
    ]);

    useEffect(() => {
        if (!selectedReadOnlyVersionId) {
            return;
        }
        if (!versionHistoryOptions.some((version) => version.documentScanTypeVersionId === selectedReadOnlyVersionId)) {
            setSelectedReadOnlyVersionId(null);
        }
    }, [versionHistoryOptions, selectedReadOnlyVersionId]);

    useEffect(() => {
        if (!selectedOverrideVersionId) {
            return;
        }
        if (
            !facilityOverrideHistoryOptions.some(
                (version) => version.facilityDocumentScanTypeVersionId === selectedOverrideVersionId,
            )
        ) {
            setSelectedOverrideVersionId(null);
        }
    }, [facilityOverrideHistoryOptions, selectedOverrideVersionId]);

    const updateForm = (updates: Partial<DraftFormState> | ((current: DraftFormState) => DraftFormState)) => {
        if (!draft || !isEditing) {
            return;
        }
        setTestResult(null);
        setForm((current) => {
            if (!current) {
                return current;
            }
            return typeof updates === 'function' ? updates(current) : { ...current, ...updates };
        });
    };

    const updateOverrideFormState = (updates: FacilityOverrideFormUpdate) => {
        if (!isEditingOverride) {
            return;
        }
        setTestResult(null);
        setOverrideForm((current) => {
            if (!current) {
                return current;
            }
            return typeof updates === 'function' ? updates(current) : { ...current, ...updates };
        });
    };

    const handleEdit = () => {
        if (draft) {
            setIsEditing(true);
            setForm(buildDraftForm(draft));
            setActiveSection('overview');
            setSelectedReadOnlyVersionId(null);
            setVersionHistoryOpen(false);
            return;
        }

        createDraft(documentScanTypeId, {
            onSuccess: (draftVersion: DocumentScanTypeVersionSummary) => {
                setLocalDraft(draftVersion);
                setForm(buildDraftForm(draftVersion));
                setIsEditing(true);
                setActiveSection('overview');
                setSelectedReadOnlyVersionId(null);
                setVersionHistoryOpen(false);
                ToastStoreObject.show('Draft created');
            },
            onError: (createError: unknown) => ToastStoreObject.show(getErrorMessage(createError)),
        });
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (scanType?.publishedVersion) {
            setSelectedReadOnlyVersionId(null);
            setForm(buildDraftForm(scanType.publishedVersion));
        } else if (draft) {
            setSelectedReadOnlyVersionId(draft.documentScanTypeVersionId);
            setForm(buildDraftForm(draft));
        }
        setTestResult(null);
        setVersionHistoryOpen(false);
    };

    const saveDraft = async (showToast = true) => {
        if (!form || !draftVersionId) {
            return false;
        }

        try {
            await updateDraft({
                documentScanTypeId,
                documentScanTypeVersionId: draftVersionId,
                data: getDraftPayload(form, scanType?.supportedEntityType || 'encounter'),
            });
            if (showToast) {
                ToastStoreObject.show('Draft saved');
            }
            return true;
        } catch (saveError) {
            ToastStoreObject.show(getErrorMessage(saveError));
            return false;
        }
    };

    const handleTestImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            setTestImageBase64(null);
            setTestImageName('');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = `${reader.result || ''}`;
            setTestImageBase64(result.includes(',') ? result.split(',')[1] : result);
            setTestImageName(file.name);
        };
        reader.onerror = () => ToastStoreObject.show('Unable to read the selected test image');
        reader.readAsDataURL(file);
    };

    const handleRunTest = async (facilityId: number | null = null) => {
        const targetVersion = isFacilityOverrideScope ? displayVersion : isEditing ? draft : displayVersion;
        if (!targetVersion) {
            ToastStoreObject.show('No document type version is available to test');
            return;
        }
        if (!isFacilityOverrideScope && isEditing && !(await saveDraft(false))) {
            return;
        }

        try {
            const result = await testVersion({
                documentScanTypeId,
                documentScanTypeVersionId: targetVersion.documentScanTypeVersionId,
                facilityId,
                imageBase64: testImageBase64,
            });
            setTestResult(result);
            setTestOpen(true);
        } catch (testError) {
            ToastStoreObject.show(getErrorMessage(testError));
        }
    };

    const handlePublish = async () => {
        if (!(await saveDraft(false))) {
            return;
        }

        try {
            await publishDraft({ documentScanTypeId, documentScanTypeVersionId: draftVersionId });
            ToastStoreObject.show('Document type published');
            setIsEditing(false);
            setLocalDraft(null);
            setSelectedReadOnlyVersionId(null);
            setVersionHistoryOpen(false);
            refetch();
        } catch (publishError) {
            ToastStoreObject.show(getErrorMessage(publishError));
        }
    };

    const ensureOverrideDraft = async () => {
        if (!selectedOverrideFacility) {
            throw new Error('Choose a facility before editing an override');
        }
        if (selectedOverrideFacility.draftVersion) {
            return selectedOverrideFacility;
        }

        const result = await createOverrideDraft({
            documentScanTypeId,
            facilityId: selectedOverrideFacility.facilityId,
        });
        const nextFacility = {
            ...selectedOverrideFacility,
            facilityDocumentScanTypeId:
                result.facilityDocumentScanType?.facilityDocumentScanTypeId ||
                selectedOverrideFacility.facilityDocumentScanTypeId,
            draftVersion: result.draftVersion,
            hasDraft: true,
        };
        setLocalOverrideDrafts((current) => ({
            ...current,
            [selectedOverrideFacility.facilityId]: {
                facilityDocumentScanTypeId: nextFacility.facilityDocumentScanTypeId,
                draftVersion: nextFacility.draftVersion,
                hasDraft: true,
            },
        }));
        if (form) {
            setOverrideForm(buildOverrideForm(nextFacility, form));
        }
        await refetchAvailability();
        return nextFacility;
    };

    const handleEditFacilityOverride = async () => {
        if (!selectedOverrideFacility) {
            return;
        }

        try {
            setIsSavingOverride(true);
            const hadDraft = !!selectedOverrideFacility.draftVersion;
            const editableFacility = await ensureOverrideDraft();
            if (form) {
                setOverrideForm(buildOverrideForm(editableFacility, form));
            }
            if (!hadDraft && editableFacility.draftVersion) {
                setCreatedOverrideDraftForEdit({
                    facilityId: editableFacility.facilityId,
                    facilityDocumentScanTypeVersionId: editableFacility.draftVersion.facilityDocumentScanTypeVersionId,
                });
            } else {
                setCreatedOverrideDraftForEdit(null);
            }
            setSelectedOverrideVersionId(null);
            setFacilityOverrideHistoryOpen(false);
            setIsEditingOverride(true);
        } catch (editError) {
            ToastStoreObject.show(getErrorMessage(editError));
        } finally {
            setIsSavingOverride(false);
        }
    };

    const handleCancelFacilityOverrideEdit = async () => {
        const draftToDiscard = createdOverrideDraftForEdit;

        try {
            if (draftToDiscard) {
                setIsSavingOverride(true);
                await discardOverrideDraft({
                    documentScanTypeId,
                    facilityId: draftToDiscard.facilityId,
                    facilityDocumentScanTypeVersionId: draftToDiscard.facilityDocumentScanTypeVersionId,
                });
                setLocalOverrideDrafts((current) => {
                    const next = { ...current };
                    delete next[draftToDiscard.facilityId];
                    return next;
                });
                await refetchAvailability();
            }
        } catch (discardError) {
            ToastStoreObject.show(getErrorMessage(discardError));
        } finally {
            setCreatedOverrideDraftForEdit(null);
            setIsEditingOverride(false);
            if (selectedOverrideFacility && form) {
                const displayFacility = draftToDiscard
                    ? { ...selectedOverrideFacility, draftVersion: null, hasDraft: false }
                    : selectedOverrideFacility;
                setOverrideForm(buildOverrideForm(displayFacility, form, selectedOverrideVersion));
            }
            setTestResult(null);
            setIsSavingOverride(false);
        }
    };

    const saveFacilityOverride = async (publish = false) => {
        if (!overrideForm) {
            return false;
        }

        try {
            setIsSavingOverride(true);
            const editableFacility = await ensureOverrideDraft();
            const draftVersion = editableFacility.draftVersion;
            if (!draftVersion) {
                throw new Error('Facility override draft was not created');
            }
            const updatedDraftVersion = await updateOverrideDraft({
                documentScanTypeId,
                facilityId: editableFacility.facilityId,
                facilityDocumentScanTypeVersionId: draftVersion.facilityDocumentScanTypeVersionId,
                data: {
                    overrides: buildOverridePayload(
                        overrideForm,
                        scanType?.supportedEntityType || 'encounter',
                        form?.allowEntityCreation || false,
                    ),
                },
            });
            const nextDraftVersion = updatedDraftVersion?.facilityDocumentScanTypeVersionId
                ? updatedDraftVersion
                : draftVersion;
            setLocalOverrideDrafts((current) => ({
                ...current,
                [editableFacility.facilityId]: {
                    facilityDocumentScanTypeId: editableFacility.facilityDocumentScanTypeId,
                    draftVersion: nextDraftVersion,
                    hasDraft: true,
                },
            }));

            if (publish) {
                await publishOverrideDraft({
                    documentScanTypeId,
                    facilityId: editableFacility.facilityId,
                    facilityDocumentScanTypeVersionId: draftVersion.facilityDocumentScanTypeVersionId,
                });
                ToastStoreObject.show('Facility override published');
                setIsEditingOverride(false);
                setLocalOverrideDrafts((current) => {
                    const next = { ...current };
                    delete next[editableFacility.facilityId];
                    return next;
                });
            } else {
                ToastStoreObject.show('Facility override draft saved');
                setIsEditingOverride(false);
            }
            setCreatedOverrideDraftForEdit(null);
            await refetchAvailability();
            return true;
        } catch (saveError) {
            ToastStoreObject.show(getErrorMessage(saveError));
            return false;
        } finally {
            setIsSavingOverride(false);
        }
    };

    const publishSavedFacilityOverride = async () => {
        if (!selectedOverrideFacility?.draftVersion) {
            return;
        }

        try {
            setIsSavingOverride(true);
            await publishOverrideDraft({
                documentScanTypeId,
                facilityId: selectedOverrideFacility.facilityId,
                facilityDocumentScanTypeVersionId:
                    selectedOverrideFacility.draftVersion.facilityDocumentScanTypeVersionId,
            });
            ToastStoreObject.show('Facility override published');
            setLocalOverrideDrafts((current) => {
                const next = { ...current };
                delete next[selectedOverrideFacility.facilityId];
                return next;
            });
            setSelectedOverrideVersionId(null);
            setFacilityOverrideHistoryOpen(false);
            setCreatedOverrideDraftForEdit(null);
            await refetchAvailability();
        } catch (publishError) {
            ToastStoreObject.show(getErrorMessage(publishError));
        } finally {
            setIsSavingOverride(false);
        }
    };

    const clearFacilityOverride = async () => {
        if (!selectedOverrideFacility) {
            return;
        }

        try {
            setIsSavingOverride(true);
            const editableFacility = await ensureOverrideDraft();
            const draftVersion = editableFacility.draftVersion;
            if (!draftVersion) {
                throw new Error('Facility override draft was not created');
            }
            await updateOverrideDraft({
                documentScanTypeId,
                facilityId: editableFacility.facilityId,
                facilityDocumentScanTypeVersionId: draftVersion.facilityDocumentScanTypeVersionId,
                data: { overrides: {} },
            });
            await publishOverrideDraft({
                documentScanTypeId,
                facilityId: editableFacility.facilityId,
                facilityDocumentScanTypeVersionId: draftVersion.facilityDocumentScanTypeVersionId,
            });
            if (form) {
                setOverrideForm({
                    ...buildOverrideForm(editableFacility, form),
                    sectionEnabled: { prompt: false, extraction: false, mapping: false },
                });
            }
            setLocalOverrideDrafts((current) => {
                const next = { ...current };
                delete next[editableFacility.facilityId];
                return next;
            });
            setSelectedOverrideVersionId(null);
            setFacilityOverrideHistoryOpen(false);
            ToastStoreObject.show('Facility override cleared');
            setIsEditingOverride(false);
            setClearOverrideConfirmOpen(false);
            setCreatedOverrideDraftForEdit(null);
            await refetchAvailability();
        } catch (clearError) {
            ToastStoreObject.show(getErrorMessage(clearError));
        } finally {
            setIsSavingOverride(false);
        }
    };

    const handleScopeSelectChange = (value: string) => {
        setIsEditing(false);
        setIsEditingOverride(false);
        setScopeMenuOpen(false);
        setScopeSearchTerm('');
        setVersionHistoryOpen(false);
        setFacilityOverrideHistoryOpen(false);
        setClearOverrideConfirmOpen(false);
        setCreatedOverrideDraftForEdit(null);
        setSelectedOverrideVersionId(null);
        if (value === 'org') {
            setSelectedOverrideFacilityId(null);
            return;
        }

        const match = value.match(/^facility:(\d+)$/);
        if (!match) {
            return;
        }
        setSelectedOverrideFacilityId(Number(match[1]));
    };

    const renderActiveSection = () => {
        if (!scanType || !form) {
            return null;
        }

        const editable = !!draft && isEditing;
        if (isFacilityOverrideScope) {
            const overrideEditable = !!selectedOverrideFacility && isEditingOverride;
            if (!selectedOverrideFacility || !overrideForm) {
                return <div className={styles.emptyState}>No facility is available for this document type.</div>;
            }
            if (activeSection === 'overview') {
                return <OrgDefaultsSection form={form} editable={false} onChange={updateForm} />;
            }
            if (activeSection === 'extraction') {
                return (
                    <FacilityExtractionOverrideSection
                        form={overrideForm}
                        basePromptText={form.promptText}
                        editable={overrideEditable}
                        onChange={updateOverrideFormState}
                    />
                );
            }
            if (activeSection === 'mapping') {
                return (
                    <FacilityMappingOverrideSection
                        scanType={scanType}
                        form={overrideForm}
                        allowEntityCreation={form.allowEntityCreation}
                        editable={overrideEditable}
                        onChange={updateOverrideFormState}
                    />
                );
            }
        }
        if (activeSection === 'overview') {
            return <OrgDefaultsSection form={form} editable={editable} onChange={updateForm} />;
        }
        if (activeSection === 'extraction') {
            return <ExtractionSection form={form} editable={editable} onChange={updateForm} />;
        }
        if (activeSection === 'mapping') {
            return <MappingSection scanType={scanType} form={form} editable={editable} onChange={updateForm} />;
        }
        return null;
    };

    const facilityHasEnabledOverrides = hasEnabledOverrideSections(overrideForm);
    const selectedOverrideFacilityEnabled = selectedOverrideFacility?.captureAiScanTypesEnabled === true;
    const isFacilityRemovalDraft = !!selectedOverrideFacility?.publishedVersion?.hasOverride && !facilityHasEnabledOverrides;
    const canPublishFacilityOverride =
        selectedOverrideFacilityEnabled &&
        !!overrideForm &&
        (facilityHasEnabledOverrides || !!selectedOverrideFacility?.publishedVersion?.hasOverride);
    const canClearFacilityOverride =
        selectedOverrideFacilityEnabled &&
        (!!selectedOverrideFacility?.draftVersion?.hasOverride || !!selectedOverrideFacility?.publishedVersion?.hasOverride);
    const testTargetVersion = isFacilityOverrideScope ? displayVersion : isEditing ? draft : displayVersion;
    const scopeMenuDisabled = isEditing || isEditingOverride;
    const selectedScopeTitle = selectedOverrideFacility ? getFacilityLabel(selectedOverrideFacility) : 'Organization Defaults';
    const orgVersionBannerText = isEditing
        ? `Editing unpublished draft version ${displayVersion?.versionNumber}.`
        : isViewingDraft
        ? `Viewing current unpublished draft version ${displayVersion?.versionNumber}.`
        : isViewingHistoricalVersion
        ? `Viewing previously published version ${displayVersion?.versionNumber}.`
        : `Viewing current published version ${displayVersion?.versionNumber}.`;
    const facilityVersionBannerText = selectedOverrideFacility
        ? selectedOverrideVersion
            ? selectedOverrideVersion.hasOverride
                ? `Viewing override version ${selectedOverrideVersion.versionNumber}.`
                : `Viewing removal version ${selectedOverrideVersion.versionNumber}.`
            : isEditingOverride
            ? selectedOverrideFacility.draftVersion
                ? selectedOverrideFacility.publishedVersion?.hasOverride && !selectedOverrideFacility.draftVersion.hasOverride
                    ? `Editing removal draft version ${selectedOverrideFacility.draftVersion.versionNumber}.`
                    : `Editing override draft version ${selectedOverrideFacility.draftVersion.versionNumber}.`
                : 'Editing override draft.'
            : selectedOverrideFacility.draftVersion
            ? selectedOverrideFacility.publishedVersion?.hasOverride && !selectedOverrideFacility.draftVersion.hasOverride
                ? `Viewing removal draft version ${selectedOverrideFacility.draftVersion.versionNumber}.`
                : `Viewing override draft version ${selectedOverrideFacility.draftVersion.versionNumber}.`
            : selectedOverrideFacility.publishedVersion?.hasOverride
            ? `Viewing published override version ${selectedOverrideFacility.publishedVersion.versionNumber}.`
            : orgVersionBannerText
        : 'Choose a facility.';
    const scopeSelect = scanType && !isEncounterFormScanType ? (
        <div className={styles.scopeSelectWrap}>
            <button
                type="button"
                className={styles.scopeButton}
                onClick={() => {
                    if (scopeMenuDisabled) {
                        return;
                    }
                    setVersionHistoryOpen(false);
                    setFacilityOverrideHistoryOpen(false);
                    if (scopeMenuOpen) {
                        setScopeSearchTerm('');
                    }
                    setScopeMenuOpen(!scopeMenuOpen);
                }}
                disabled={scopeMenuDisabled}
                aria-expanded={scopeMenuOpen}
                aria-label={`Configuration scope: ${selectedScopeTitle}`}
                data-test-id="document-type-scope-button"
            >
                <strong>{selectedScopeTitle}</strong>
            </button>
            {scopeMenuOpen ? (
                <div
                    className={classNames(styles.versionHistoryMenu, styles.scopeMenu)}
                    data-test-id="document-type-scope-menu"
                >
                    <button
                        type="button"
                        className={classNames(styles.versionHistoryItem, {
                            [styles.versionHistoryItemActive]: selectedOverrideFacilityId === null,
                        })}
                        onClick={() => handleScopeSelectChange('org')}
                        data-test-id="document-type-scope-option-org"
                    >
                        <strong>Organization Defaults</strong>
                        <span>Shared configuration for this document type</span>
                    </button>
                    <div className={styles.scopeMenuDivider}>Facility Overrides</div>
                    <input
                        type="search"
                        value={scopeSearchTerm}
                        onChange={(event) => setScopeSearchTerm(event.target.value)}
                        placeholder="Search facilities"
                        className={styles.scopeSearchInput}
                        data-test-id="document-type-scope-search"
                    />
                    <div className={styles.scopeOptionList}>
                        {filteredScopeFacilities.length ? (
                            filteredScopeFacilities.map((facility) => {
                                const draftIndicatorLabel = getFacilityDraftIndicatorLabel(facility);
                                return (
                                    <button
                                        key={facility.facilityId}
                                        type="button"
                                        className={classNames(styles.versionHistoryItem, {
                                            [styles.versionHistoryItemActive]:
                                                selectedOverrideFacilityId === facility.facilityId,
                                            [styles.versionHistoryItemDisabled]:
                                                !facility.captureAiScanTypesEnabled,
                                        })}
                                        onClick={() => handleScopeSelectChange(`facility:${facility.facilityId}`)}
                                        data-test-id={`document-type-scope-option-facility-${facility.facilityId}`}
                                    >
                                        <strong>{getFacilityLabel(facility)}</strong>
                                        <div className={styles.scopeOptionMeta}>
                                            <span>{getFacilityScopeSubtitle(facility)}</span>
                                            {draftIndicatorLabel ? (
                                                <span
                                                    className={styles.scopeDraftIndicator}
                                                    title={draftIndicatorLabel}
                                                    aria-label={draftIndicatorLabel}
                                                    data-test-id={`document-type-scope-draft-indicator-${facility.facilityId}`}
                                                >
                                                    <Icon
                                                        name={ICONS.Pencil2}
                                                        size={11}
                                                        color={variables.darkYellow}
                                                    />
                                                </span>
                                            ) : null}
                                        </div>
                                    </button>
                                );
                            })
                        ) : (
                            <div className={styles.scopeEmptyState} data-test-id="document-type-scope-empty-state">
                                No enabled facilities match.
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    ) : scanType ? (
        <span className={styles.scopeStaticLabel}>Organization Defaults</span>
    ) : null;

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex direction="column" className={styles.container}>
                    <Flex direction="row" align="center" justify="between" className={styles.header}>
                        <Flex direction="column">
                            <Button
                                type="transparent"
                                text="Back to Document Types"
                                onClick={() => history.push(ROUTES.getString(ROUTES.DocumentTypes))}
                                className={styles.backButton}
                                data-test-id="document-type-detail-back-button"
                            />
                            <h1 className={styles.pageTitle}>{pageTitle}</h1>
                            <div className={styles.headerChips}>
                                <StatusChip
                                    text={
                                        isEditing
                                            ? 'Editing Draft'
                                            : draft
                                            ? 'Unpublished Draft'
                                            : scanType?.publishedVersion
                                            ? 'Published'
                                            : 'No Draft'
                                    }
                                    tone={isEditing || draft ? 'warning' : scanType?.publishedVersion ? 'good' : 'neutral'}
                                    data-test-id="document-type-detail-status-chip"
                                />
                                {scanType ? <StatusChip text={formatValue(scanType.supportedEntityType)} /> : null}
                                {scanType?.publishedVersion ? (
                                    <StatusChip text={`Published v${scanType.publishedVersion.versionNumber}`} />
                                ) : null}
                            </div>
                        </Flex>
                        {!isLoading && scanType ? (
                            <Flex direction="row" align="center" className={styles.headerActions}>
                                <HeaderIconButton
                                    title="Settings"
                                    icon={<Icon name={ICONS.Cog3} size={18} color={variables.darkBlue} />}
                                    onClick={() => setSettingsOpen(true)}
                                    data-test-id="document-type-settings-button"
                                />
                                {!isEditing && !isEditingOverride ? (
                                    <HeaderIconButton
                                        title="Test"
                                        icon={<Icon name={ICONS.CheckCircle} size={18} color={variables.darkBlue} />}
                                        onClick={() => setTestOpen(true)}
                                        data-test-id="open-document-type-test-button"
                                    />
                                ) : null}
                            </Flex>
                        ) : null}
                    </Flex>

                    {error && (
                        <Card className={styles.errorCard} data-test-id="document-type-detail-error-card">
                            <div className={styles.errorMessage}>
                                <strong>Error loading document type:</strong> {parseError(error)}
                                <Button
                                    type="small"
                                    text="Retry"
                                    onClick={() => refetch()}
                                    className={styles.retryButton}
                                    data-test-id="document-type-detail-retry-button"
                                />
                            </div>
                        </Card>
                    )}

                    {isLoading && <div className={styles.emptyState}>Loading document type...</div>}

                    {!isLoading && scanType && (
                        <>
                            {displayVersion ? (
                                <div className={styles.versionBanner} data-test-id="document-type-version-banner">
                                    <div className={styles.versionBannerText}>
                                        {scopeSelect}
                                        {isFacilityOverrideScope ? (
                                            <>
                                                <span data-test-id="document-type-version-banner-message">
                                                    {facilityVersionBannerText}
                                                </span>
                                                {selectedOverrideFacility &&
                                                !selectedOverrideFacility.captureAiScanTypesEnabled ? (
                                                    <span data-test-id="facility-override-disabled-message">
                                                        Capture AI document types not enabled for this facility.
                                                    </span>
                                                ) : null}
                                            </>
                                        ) : (
                                            <>
                                                <span data-test-id="document-type-version-banner-message">
                                                    {orgVersionBannerText}
                                                </span>
                                                {isViewingDraft && scanType.publishedVersion ? (
                                                    <button
                                                        type="button"
                                                        className={styles.versionBannerLink}
                                                        onClick={() => {
                                                            setSelectedReadOnlyVersionId(
                                                                scanType.publishedVersion?.documentScanTypeVersionId || null,
                                                            );
                                                            setVersionHistoryOpen(false);
                                                        }}
                                                        data-test-id="view-current-published-version-button"
                                                    >
                                                        View current published version
                                                    </button>
                                                ) : null}
                                                {isViewingCurrentPublished && draft ? (
                                                    <button
                                                        type="button"
                                                        className={styles.versionBannerLink}
                                                        onClick={() => {
                                                            setSelectedReadOnlyVersionId(draft.documentScanTypeVersionId);
                                                            setVersionHistoryOpen(false);
                                                        }}
                                                        data-test-id="view-current-draft-version-button"
                                                    >
                                                        View current unpublished draft
                                                    </button>
                                                ) : null}
                                            </>
                                        )}
                                    </div>
                                    <div className={styles.versionBannerActions}>
                                        {!isFacilityOverrideScope && !isEditing ? (
                                            <div className={styles.versionHistoryWrap}>
                                                <HeaderIconButton
                                                    title="Version history"
                                                    icon={
                                                        <Icon
                                                            name={ICONS.TimeClockCircle1Alternate}
                                                            size={16}
                                                            color={variables.darkBlue}
                                                        />
                                                    }
                                                    onClick={() => setVersionHistoryOpen((current) => !current)}
                                                    disabled={versionHistoryOptions.length === 0}
                                                    data-test-id="document-type-version-history-button"
                                                />
                                                {versionHistoryOpen ? (
                                                    <div
                                                        className={styles.versionHistoryMenu}
                                                        data-test-id="document-type-version-history-menu"
                                                    >
                                                        {versionHistoryOptions.map((version) => {
                                                            const isCurrentPublished =
                                                                version.documentScanTypeVersionId ===
                                                                scanType.publishedVersion?.documentScanTypeVersionId;
                                                            const isCurrentDraft =
                                                                version.documentScanTypeVersionId ===
                                                                draft?.documentScanTypeVersionId;
                                                            const isSelected =
                                                                version.documentScanTypeVersionId ===
                                                                displayVersion?.documentScanTypeVersionId;
                                                            return (
                                                                <button
                                                                    key={version.documentScanTypeVersionId}
                                                                    className={classNames(styles.versionHistoryItem, {
                                                                        [styles.versionHistoryItemActive]: isSelected,
                                                                    })}
                                                                    onClick={() => {
                                                                        setSelectedReadOnlyVersionId(
                                                                            version.documentScanTypeVersionId,
                                                                        );
                                                                        setIsEditing(false);
                                                                        setVersionHistoryOpen(false);
                                                                    }}
                                                                    data-test-id={`document-type-version-history-option-${version.versionNumber}`}
                                                                >
                                                                    <strong>
                                                                        Version {version.versionNumber}
                                                                        {isCurrentDraft ? ' (draft)' : ''}
                                                                        {isCurrentPublished ? ' (current)' : ''}
                                                                    </strong>
                                                                    <span>{scanType.displayName}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        {isFacilityOverrideScope && !isEditingOverride ? (
                                            <div className={styles.versionHistoryWrap}>
                                                <HeaderIconButton
                                                    title="Override history"
                                                    icon={
                                                        <Icon
                                                            name={ICONS.TimeClockCircle1Alternate}
                                                            size={16}
                                                            color={variables.darkBlue}
                                                        />
                                                    }
                                                    onClick={() => setFacilityOverrideHistoryOpen((current) => !current)}
                                                    disabled={facilityOverrideHistoryOptions.length === 0}
                                                    data-test-id="facility-override-history-button"
                                                />
                                                {facilityOverrideHistoryOpen ? (
                                                    <div
                                                        className={styles.versionHistoryMenu}
                                                        data-test-id="facility-override-history-menu"
                                                    >
                                                        {facilityOverrideHistoryOptions.map((version) => {
                                                            const isCurrentPublished =
                                                                version.facilityDocumentScanTypeVersionId ===
                                                                selectedOverrideFacility?.publishedVersion
                                                                    ?.facilityDocumentScanTypeVersionId;
                                                            const isCurrentDraft =
                                                                version.facilityDocumentScanTypeVersionId ===
                                                                selectedOverrideFacility?.draftVersion
                                                                    ?.facilityDocumentScanTypeVersionId;
                                                            const activeVersionId =
                                                                selectedOverrideVersionId ||
                                                                selectedOverrideFacility?.draftVersion
                                                                    ?.facilityDocumentScanTypeVersionId ||
                                                                selectedOverrideFacility?.publishedVersion
                                                                    ?.facilityDocumentScanTypeVersionId;
                                                            const isSelected =
                                                                version.facilityDocumentScanTypeVersionId === activeVersionId;
                                                            return (
                                                                <button
                                                                    key={version.facilityDocumentScanTypeVersionId}
                                                                    className={classNames(styles.versionHistoryItem, {
                                                                        [styles.versionHistoryItemActive]: isSelected,
                                                                    })}
                                                                    onClick={() => {
                                                                        setSelectedOverrideVersionId(
                                                                            version.facilityDocumentScanTypeVersionId,
                                                                        );
                                                                        setIsEditingOverride(false);
                                                                        setFacilityOverrideHistoryOpen(false);
                                                                    }}
                                                                    data-test-id={`facility-override-history-option-${version.versionNumber}`}
                                                                >
                                                                    <strong>
                                                                        Version {version.versionNumber}
                                                                        {isCurrentDraft ? ' (draft)' : ''}
                                                                        {isCurrentPublished ? ' (current)' : ''}
                                                                        {!version.hasOverride ? ' (removal)' : ''}
                                                                    </strong>
                                                                    <span>{getFacilityLabel(selectedOverrideFacility)}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        {isFacilityOverrideScope ? (
                                            !isEditingOverride ? (
                                                <>
                                                    {selectedOverrideFacility?.draftVersion && canPublishFacilityOverride ? (
                                                        <Button
                                                            type="primary"
                                                            text={isFacilityRemovalDraft ? 'Publish Removal' : 'Publish Override'}
                                                            onClick={publishSavedFacilityOverride}
                                                            isLoading={isSavingOverride}
                                                            className={styles.bannerActionButton}
                                                            data-test-id="publish-facility-override-button"
                                                        />
                                                    ) : null}
                                                    {canClearFacilityOverride ? (
                                                        <Button
                                                            type="outline"
                                                            text="Clear Override"
                                                            onClick={() => setClearOverrideConfirmOpen(true)}
                                                            isLoading={isSavingOverride}
                                                            className={styles.bannerActionButton}
                                                            data-test-id="clear-facility-override-button"
                                                        />
                                                    ) : null}
                                                    <Button
                                                        type="primary"
                                                        text="Edit"
                                                        onClick={handleEditFacilityOverride}
                                                        isLoading={isSavingOverride}
                                                        disabled={!selectedOverrideFacilityEnabled}
                                                        className={styles.bannerActionButton}
                                                        data-test-id="edit-facility-override-button"
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        type="outline"
                                                        text="Cancel"
                                                        onClick={handleCancelFacilityOverrideEdit}
                                                        className={styles.bannerActionButton}
                                                        data-test-id="cancel-facility-override-edit-button"
                                                    />
                                                    <Button
                                                        type="outline"
                                                        text="Save Override Draft"
                                                        onClick={() => saveFacilityOverride(false)}
                                                        isLoading={isSavingOverride}
                                                        className={styles.bannerActionButton}
                                                        data-test-id="save-facility-override-button"
                                                    />
                                                </>
                                            )
                                        ) : !isEditing ? (
                                            <Button
                                                type="primary"
                                                text="Edit"
                                                onClick={handleEdit}
                                                isLoading={isCreatingDraft}
                                                className={styles.bannerActionButton}
                                                data-test-id="edit-document-type-button"
                                            />
                                        ) : (
                                            <>
                                                <Button
                                                    type="outline"
                                                    text="Cancel"
                                                    onClick={handleCancelEdit}
                                                    className={styles.bannerActionButton}
                                                    data-test-id="cancel-document-type-edit-button"
                                                />
                                                <Button
                                                    type="outline"
                                                    text="Save Draft"
                                                    onClick={() => saveDraft()}
                                                    isLoading={isSaving}
                                                    className={styles.bannerActionButton}
                                                    data-test-id="save-document-type-draft-button"
                                                />
                                                <Button
                                                    type="primary"
                                                    text="Publish"
                                                    onClick={handlePublish}
                                                    isLoading={isPublishing}
                                                    className={styles.bannerActionButton}
                                                    data-test-id="publish-document-type-draft-button"
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                            <div
                                className={styles.detailWorkspace}
                                data-test-id={isFacilityOverrideScope ? 'facility-override-scope' : undefined}
                            >
                                <nav className={styles.leftRail} data-test-id="document-type-detail-left-rail">
                                    {visibleDetailSections.map((section) => (
                                        <button
                                            key={section.id}
                                            className={classNames(styles.railItem, {
                                                [styles.railItemActive]: activeSection === section.id,
                                            })}
                                            onClick={() => setActiveSection(section.id)}
                                            data-test-id={`document-type-section-${section.id}`}
                                        >
                                            <strong>
                                                {section.id === 'mapping'
                                                    ? getEntityMappingTitle(scanType.supportedEntityType)
                                                    : section.title}
                                            </strong>
                                            <span>{section.description}</span>
                                        </button>
                                    ))}
                                </nav>
                                <main className={styles.detailMain}>{renderActiveSection()}</main>
                            </div>
                            <DocumentTypeSettingsModal
                                isOpen={settingsOpen}
                                onClose={() => setSettingsOpen(false)}
                                scanType={scanType}
                                documentScanTypeId={documentScanTypeId}
                                onSaved={() => refetch()}
                            />
                            <TestModal
                                isOpen={testOpen}
                                onClose={() => setTestOpen(false)}
                                targetVersion={testTargetVersion}
                                availability={availability}
                                testImageName={testImageName}
                                isTesting={isTesting}
                                testResult={testResult}
                                form={form}
                                currentFacilityId={isFacilityOverrideScope ? selectedOverrideFacility?.facilityId ?? null : null}
                                onTestImageChange={handleTestImageChange}
                                onRunTest={handleRunTest}
                            />
                            {clearOverrideConfirmOpen && selectedOverrideFacility ? (
                                <div data-test-id="clear-facility-override-confirmation">
                                    <ConfirmationModal
                                        headerTitle="Clear Override?"
                                        title={`Clear the override for ${getFacilityLabel(
                                            selectedOverrideFacility,
                                        )}? This publishes an empty override version so the facility uses organization defaults.`}
                                        confirmButtonText="Clear Override"
                                        cancelButtonText="Cancel"
                                        isLoading={isSavingOverride}
                                        onCancel={() => setClearOverrideConfirmOpen(false)}
                                        onConfirm={clearFacilityOverride}
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                </Flex>
            </PageContent>
        </PageContainer>
    );
}
